import React, { useState, useEffect } from 'react';
import { patientAPI } from '../../../api/patient';
import { getHospitalSettings } from '../../../api/hospitalSettings';
import appointmentAPI from '../../../api/appointment';
import prescriptionAPI from '../../../api/prescription';
import claimFormBAPI from '../../../api/claimFormB';
import claimFormAAPI from '../../../api/claimFormA';
import { userAPI } from '../../../api/user';
import './ClaimFormB.css';
import { 
  Search, 
  User, 
  Building2, 
  FileText, 
  Save, 
  Printer,
  CheckSquare,
  AlertCircle,
  Calendar,
  ChevronDown,
  List,
  Eye,
  Trash2,
  Send,
  Download,
  CreditCard,
  Shield,
  History,
  Receipt
} from 'lucide-react';
import { toast } from 'react-toastify';
import { generateClaimFormBPdf, downloadClaimFormBPdf } from '../utils/generateClaimFormBPdf';
import { generateClaimFormAPdf, downloadClaimFormAPdf } from '../utils/generateClaimFormAPdf';

const ClaimFormB = ({ showToast }) => {
  const [loading, setLoading] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientAppointments, setPatientAppointments] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [hospitalDetails, setHospitalDetails] = useState(null);
  const [availableDiseases, setAvailableDiseases] = useState([]); // Diseases from prescriptions
  
  // Ref to store hospital city for reliable access in print functions
  const hospitalCityRef = React.useRef('');
  
  // Form type state
  const [formType, setFormType] = useState('B'); // 'A' or 'B'
  
  // Tab state
  const [activeTab, setActiveTab] = useState('new'); // 'new' or 'saved'
  
  // Saved forms state
  const [savedForms, setSavedForms] = useState([]);
  const [loadingSavedForms, setLoadingSavedForms] = useState(false);
  const [savedFormsPage, setSavedFormsPage] = useState(1);
  const [savedFormsPagination, setSavedFormsPagination] = useState(null);
  const [savedFormsSearch, setSavedFormsSearch] = useState('');
  const [viewingForm, setViewingForm] = useState(null); // Form being viewed
  
  // Modal states for custom popups
  const [deleteModal, setDeleteModal] = useState({ show: false, formId: null, formType: null });
  const [downloadModal, setDownloadModal] = useState({ show: false, form: null });

  // Form state with all fields from the cashless claim form
  const [formData, setFormData] = useState({
    // A. Hospital Details (auto-populated from hospital settings)
    hospitalName: '',
    hospitalId: '',
    treatingDoctor: '',
    doctorQualification: '',
    hospitalType: 'network', // network or non-network
    stateRegistrationNo: '',
    hospitalPhone: '',

    // B. Patient Details (auto-populated from patient API)
    patientName: '',
    ipRegistrationNumber: '',
    gender: '',
    age: '',
    ageMonths: '',
    dateOfBirth: '',
    dateOfAdmission: '',
    timeOfAdmission: '',
    dateOfDischarge: '',
    timeOfDischarge: '',
    typeOfAdmission: 'planned', // emergency, planned, daycare, maternity
    
    // Maternity specific
    dateOfDelivery: '',
    gravidaStatus: '',

    // Discharge status
    dischargeStatus: 'home', // home, another_hospital, deceased
    
    totalClaimedAmount: '',

    // C. Ailment Details
    primaryDiagnosisCode: '',
    primaryDiagnosisDescription: '',
    additionalDiagnosisCode: '',
    additionalDiagnosisDescription: '',
    comorbidity1Code: '',
    comorbidity1Description: '',
    comorbidity2Code: '',
    comorbidity2Description: '',

    // Procedures
    procedure1Code: '',
    procedure1Description: '',
    procedure2Code: '',
    procedure2Description: '',
    procedure3Code: '',
    procedure3Description: '',
    procedureDetails: '',

    // Authorization
    preAuthObtained: 'no',
    preAuthNumber: '',
    authNotObtainedReason: '',

    // Injury details
    hospitalizationDueToInjury: 'no',
    injuryCause: '', // self_inflicted, road_accident, substance_abuse, others
    injuryOtherCause: '',
    substanceAbuseTestConducted: 'no',
    isMedicoLegal: 'no',
    reportedToPolice: 'no',
    policeNotReportedReason: '',
    firNumber: '',

    // D. Claim Documents Checklist
    documents: {
      claimFormSigned: false,
      preAuthRequest: false,
      preAuthApproval: false,
      photoIdVerified: false,
      dischargeSummary: false,
      otNotes: false,
      hospitalMainBill: false,
      hospitalBreakupBill: false,
      investigationReports: false,
      ctMrUsgHpeReports: false,
      doctorReferenceSlip: false,
      ecg: false,
      pharmacyBills: false,
      mlcReportsAndFir: false,
      deathSummary: false,
      others: false,
      othersSpecify: ''
    },

    // E. Non-Network Hospital Details (only if non-network)
    nonNetworkHospitalAddress: '',
    nonNetworkHospitalCity: '',
    nonNetworkHospitalState: '',
    nonNetworkHospitalPinCode: '',
    nonNetworkHospitalPhone: '',
    nonNetworkHospitalRegistrationNo: '',
    nonNetworkHospitalPan: '',
    nonNetworkInpatientBeds: '',
    nonNetworkHasFacilityOT: false,
    nonNetworkHasFacilityICU: false,
    nonNetworkOtherFacilities: '',

    // F. Hospital Declaration (auto-filled)
    declarationDate: '',
    declarationPlace: ''
  });

  // Form A state (Reimbursement Claim Form)
  const [formAData, setFormAData] = useState({
    // Section A - Details of Primary Insured
    policyNo: '',
    certificateNo: '',
    companyTpaId: '',
    insuredName: '',
    insuredAddress: '',
    insuredCity: '',
    insuredState: '',
    insuredPinCode: '',
    insuredPhone: '',
    insuredEmail: '',

    // Section B - Details of Insurance History
    coveredByOtherInsurance: 'no',
    firstInsuranceCommencementDate: '',
    otherCompanyName: '',
    otherPolicyNo: '',
    sumInsured: '',
    hospitalizedInLastFourYears: 'no',
    lastHospitalizationDate: '',
    lastHospitalizationDiagnosis: '',
    previouslyCovered: 'no',
    previousCompanyName: '',

    // Section C - Details of Insured Person Hospitalized
    patientName: '',
    patientGender: '',
    patientAge: '',
    patientAgeMonths: '',
    patientDob: '',
    relationshipToPrimary: '', // self, spouse, child, father, mother, other
    relationshipOther: '',
    patientOccupation: '', // service, self_employed, home_maker, student, retired, other
    patientOccupationOther: '',
    patientAddress: '',
    patientCity: '',
    patientState: '',
    patientPinCode: '',
    patientPhone: '',
    patientEmail: '',

    // Section D - Details of Hospitalization
    hospitalName: '',
    roomCategory: '', // day_care, single_occupancy, twin_sharing, three_or_more
    hospitalizationDueTo: '', // injury, illness, maternity
    injuryOrDiseaseDate: '',
    dateOfDelivery: '',
    dateOfAdmission: '',
    timeOfAdmission: '',
    dateOfDischarge: '',
    timeOfDischarge: '',
    injuryCause: '', // self_inflicted, road_traffic_accident, substance_abuse_alcohol
    isMedicoLegal: 'no',
    reportedToPolice: 'no',
    mlcReportAttached: 'no',
    systemOfMedicine: '',
    diagnosis: '', // Current hospitalization diagnosis
    diagnosisIcdCode: '', // ICD-10 code for diagnosis

    // Section E - Details of Claim
    preHospitalizationExpenses: '',
    hospitalizationExpenses: '',
    postHospitalizationExpenses: '',
    healthCheckupCost: '',
    ambulanceCharges: '',
    otherExpenses: '',
    otherExpensesCode: '',
    totalClaimAmount: '',
    preHospitalizationPeriod: '',
    postHospitalizationPeriod: '',

    // Domiciliary Hospitalization Claim
    claimForDomiciliary: 'no',
    domiciliaryDetails: '',

    // Lump sum / Cash benefit claimed
    hospitalDailyCash: '',
    criticalIllnessBenefit: '',
    surgicalCash: '',
    convalescence: '',
    prePostHospitalizationLumpSum: '',
    othersLumpSum: '',
    totalLumpSum: '',

    // Claim Documents Checklist
    documents: {
      claimFormSigned: false,
      claimIntimationCopy: false,
      hospitalMainBill: false,
      hospitalBreakupBill: false,
      hospitalBillPaymentReceipt: false,
      hospitalDischargeSummary: false,
      pharmacyBill: false,
      operationTheaterNotes: false,
      ecg: false,
      doctorsRequestForInvestigation: false,
      investigationReports: false,
      doctorsPrescriptions: false,
      others: false,
      othersSpecify: ''
    },

    // Section F - Details of Bills Enclosed
    bills: Array(10).fill(null).map(() => ({ 
      billNo: '',
      date: '', 
      issuedBy: '', 
      towards: '', 
      amount: '' 
    })),

    // Section G - Details of Primary Insured's Bank Account
    pan: '',
    accountNumber: '',
    bankName: '',
    bankBranch: '',
    chequePayableDetails: '',
    ifscCode: '',

    // Section H - Declaration
    declarationDate: '',
    declarationPlace: '',
    declarationSignature: ''
  });

  // Load hospital settings on mount
  useEffect(() => {
    loadHospitalSettings();
  }, []);

  // Helper function to convert UTC to IST
  const convertUTCtoIST = (utcDateString) => {
    if (!utcDateString) return { date: '', time: '' };
    
    const utcDate = new Date(utcDateString);
    
    // Convert to IST by adding 5 hours and 30 minutes
    const istDate = new Date(utcDate.getTime() + (5.5 * 60 * 60 * 1000));
    
    const date = istDate.toISOString().split('T')[0];
    const time = istDate.toISOString().split('T')[1].substring(0, 5);
    
    return { date, time };
  };

  // Helper function to get hospital city with fallback
  const getDeclarationCity = async () => {
    // 1. Check Ref (fastest)
    if (hospitalCityRef.current) return hospitalCityRef.current;
    
    // 2. Check State
    if (hospitalDetails?.address?.city) {
      hospitalCityRef.current = hospitalDetails.address.city;
      return hospitalDetails.address.city;
    }
    
    // 3. Fetch Fresh (Reliable)
    try {
      console.log('?? PDF Generation: City missing in Ref/State, fetching hospital settings...');
      const response = await getHospitalSettings();
      const hospital = response.data?.data || response.data;
      const city = hospital?.address?.city || hospital?.city || '';
      
      if (city) {
        console.log('? PDF Generation: Fetched city freshly:', city);
        hospitalCityRef.current = city;
        // Also update state if possible, but safe not to trigger re-renders inside async actions
        return city;
      }
    } catch (error) {
      console.error('? PDF Generation: Failed to fetch settings fallback:', error);
    }
    
    return '';
  };

  const loadHospitalSettings = async () => {
    try {
      const response = await getHospitalSettings();
      // Handle both response formats: {success, data: {...}} or direct data
      const hospital = response.data?.data || response.data;
      
      if (hospital) {
        setHospitalDetails(hospital);
        
        // Get today's date for declaration in DD/MM/YYYY format
        const today = new Date();
        const formattedDate = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;
        
        // Get hospital city for declaration place - robust check
        const hospitalCity = hospital.address?.city || hospital.city || '';
        
        // Store in ref for reliable access in print functions (avoids stale closure)
        hospitalCityRef.current = hospitalCity;
        
        console.log('?? Hospital settings loaded:', { 
          foundCity: hospitalCity, 
          addressObj: hospital.address,
          fullObj: hospital 
        });
        
        setFormData(prev => ({
          ...prev,
          hospitalName: hospital.hospitalName || hospital.name || '',
          hospitalId: hospital.hospitalCode || hospital.hospitalId || '',
          hospitalPhone: hospital.contactDetails?.phone || hospital.contact?.phone || hospital.phone || '',
          stateRegistrationNo: hospital.registrationNumber || hospital.registrationNo || '',
          // Section E: Non-Network Hospital Details (only fill address details, NOT OT/ICU for network)
          nonNetworkHospitalAddress: hospital.address?.street || '',
          nonNetworkHospitalCity: hospitalCity,
          nonNetworkHospitalState: hospital.address?.state || '',
          nonNetworkHospitalPinCode: hospital.address?.zipCode || '',
          nonNetworkHospitalPhone: hospital.contact?.phone || hospital.contact?.mobile || '',
          nonNetworkHospitalRegistrationNo: hospital.registrationNumber || hospital.registrationNo || '',
          nonNetworkHospitalPan: hospital.taxDetails?.panNumber || '',
          // OT and ICU should NOT be ticked for network hospitals (default is network)
          nonNetworkHasFacilityOT: false,
          nonNetworkHasFacilityICU: false,
          // Declaration section - auto-fill date and place (Form B only)
          declarationDate: formattedDate,
          declarationPlace: hospitalCity
        }));
        
        // Update formAData for Section H Declaration (don't auto-fill date for Form A)
        setFormAData(prev => ({
          ...prev,
          declarationPlace: hospitalCity
        }));
      }
    } catch (error) {
      console.error('Error loading hospital settings:', error);
    }
  };

  // Search patients
  const handlePatientSearch = async (query) => {
    setPatientSearch(query);
    if (query.length < 2) {
      setPatientSearchResults([]);
      return;
    }

    try {
      console.log('?? Searching for patients:', query);
      const response = await patientAPI.searchPatients(query);
      console.log('?? Search response:', response);
      
      // The API returns { data: { patients: [...] } }
      const patients = response.data?.patients || response.data || [];
      console.log('?? Found patients:', patients);
      
      setPatientSearchResults(patients);
      
      if (patients.length === 0) {
        showToast?.('info', 'No patients found');
      }
    } catch (error) {
      console.error('? Error searching patients:', error);
      showToast?.('error', 'Failed to search patients: ' + (error.message || 'Unknown error'));
      setPatientSearchResults([]);
    }
  };

  const selectPatient = async (patient) => {
    try {
      console.log('?? Selected patient:', patient);
      
      // Validate patient exists by making a quick API call
      const patientId = patient._id || patient.id;
      if (!patientId) {
        showToast?.('error', 'Invalid patient selected. Patient ID is missing.');
        return;
      }
      
      // Try to fetch patient details to validate existence
      try {
        await patientAPI.getPatientById(patientId);
      } catch (validationError) {
        if (validationError.response?.status === 404) {
          showToast?.('error', 'Patient not found in database. Please refresh and search again.');
          return;
        }
        console.warn('Patient validation warning:', validationError);
      }
      
      setSelectedPatient(patient);
      setPatientSearch(`${patient.firstName} ${patient.lastName} - ${patient.mobile}`);
      setPatientSearchResults([]);
      setSelectedAppointment(null);

      // Calculate age from date of birth
      const calculateAge = (dob) => {
        if (!dob) return { years: '', months: '' };
        const birthDate = new Date(dob);
        const today = new Date();
        let years = today.getFullYear() - birthDate.getFullYear();
        let months = today.getMonth() - birthDate.getMonth();
        
        if (months < 0) {
          years--;
          months += 12;
        }
        
        // Pad months with leading zero (e.g., 4 -> '04')
        const paddedMonths = months.toString().padStart(2, '0');
        
        return { years: years.toString(), months: paddedMonths };
      };

      const age = calculateAge(patient.dateOfBirth || patient.dob);
      console.log('?? Calculated age:', age);

      // Auto-populate Form B patient details
      setFormData(prev => ({
        ...prev,
        patientName: patient.fullName || `${patient.firstName || ''} ${patient.lastName || ''}`.trim(),
        ipRegistrationNumber: patient.uhid || patient.externalId || '',
        gender: (patient.gender || patient.sex || '').toLowerCase(),
        dateOfBirth: patient.dateOfBirth || patient.dob ? new Date(patient.dateOfBirth || patient.dob).toISOString().split('T')[0] : '',
        age: patient.age?.toString() || age.years,
        ageMonths: age.months
      }));

      // Auto-populate Form A patient details
      const formAUpdate = {
        ...formAData,
        // Section A - Insurance Details (auto-fill from patient profile)
        policyNo: patient.policyNumber || '',
        certificateNo: patient.certificateNumber || '',
        companyTpaId: patient.companyTpaId || '',
        firstInsuranceCommencementDate: patient.insuranceCommencementDate ? new Date(patient.insuranceCommencementDate).toISOString().split('T')[0] : '',
        sumInsured: patient.sumInsured?.toString() || '',
        // Section A - Primary Insured (use patient info if they are the primary insured)
        insuredName: patient.fullName || `${patient.firstName || ''} ${patient.lastName || ''}`.trim(),
        insuredPhone: patient.mobile || patient.phone || '',
        insuredEmail: patient.email || '',
        insuredAddress: patient.personalAddress?.street || patient.address?.street || '',
        insuredCity: patient.personalAddress?.city || patient.address?.city || '',
        insuredState: patient.personalAddress?.state || patient.address?.state || '',
        insuredPinCode: patient.personalAddress?.zipCode || patient.address?.zipCode || '',
        // Section C - Patient Details
        patientName: patient.fullName || `${patient.firstName || ''} ${patient.lastName || ''}`.trim(),
        patientGender: (patient.gender || patient.sex || '').toLowerCase(),
        patientDob: patient.dateOfBirth || patient.dob ? new Date(patient.dateOfBirth || patient.dob).toISOString().split('T')[0] : '',
        patientAge: patient.age?.toString() || age.years,
        patientAgeMonths: age.months,
        patientPhone: patient.mobile || patient.phone || '',
        patientEmail: patient.email || '',
        patientAddress: patient.personalAddress?.street || patient.address?.street || '',
        patientCity: patient.personalAddress?.city || patient.address?.city || '',
        patientState: patient.personalAddress?.state || patient.address?.state || '',
        patientPinCode: patient.personalAddress?.zipCode || patient.address?.zipCode || '',
        // Section D - Hospital Details (auto-fill from hospital settings)
        hospitalName: hospitalDetails?.name || hospitalDetails?.hospitalName || ''
      };
      console.log('?? Form A Update:', formAUpdate);
      setFormAData(formAUpdate);

      // Fetch patient's appointments
      await loadPatientAppointments(patient._id || patient.id);
      
      showToast?.('success', `Patient ${patient.firstName} ${patient.lastName} selected and validated successfully`);
    } catch (error) {
      console.error('Error selecting patient:', error);
      showToast?.('error', 'Failed to select patient. Please try again.');
    }
  };

  // Load patient appointments
  const loadPatientAppointments = async (patientId) => {
    try {
      setLoadingAppointments(true);
      
      const response = await appointmentAPI.list({ 
        patient: patientId,  // Backend expects 'patient' not 'patientId'
        includeDoctor: true,
        includePatient: true
      });
            
      // Handle different response structures
      const appointments = response.data?.items || response.data?.appointments || response.data || [];
      
      // Filter completed appointments (where prescription might exist)
      const completedAppointments = appointments.filter(apt => 
        apt.status === 'Completed' || apt.status === 'completed'
      );
      
      setPatientAppointments(completedAppointments);
      
      if (completedAppointments.length === 0) {
        toast.info('No completed appointments found for this patient');
      } else {
        toast.success(`Found ${completedAppointments.length} completed appointment(s)`);
      }
    } catch (error) {
      console.error('? Error loading appointments:', error);
      setPatientAppointments([]);
    } finally {
      setLoadingAppointments(false);
    }
  };

  // Select appointment and fetch prescription
  const selectAppointment = async (appointment) => {
    setSelectedAppointment(appointment);
    
    try {
      setLoading(true);
      
      // Fetch doctor's profile for firstName, lastName, and qualification
      let doctorFirstName = '';
      let doctorLastName = '';
      let doctorQualification = '';
      
      const doctorId = appointment.doctor?._id || appointment.doctor?.id || appointment.doctorId;
      if (doctorId) {
        try {
          const doctorProfile = await userAPI.getById(doctorId);
          const doctorData = doctorProfile?.data?.user || doctorProfile?.user || doctorProfile?.data || doctorProfile;
          console.log('????? Doctor profile:', doctorData);
          
          // Use firstName and lastName directly from user object
          doctorFirstName = doctorData?.firstName || '';
          doctorLastName = doctorData?.lastName || '';
          
          // Use qualification directly from user object (not specialization)
          doctorQualification = doctorData?.qualification || '';
        } catch (docErr) {
          console.error('Error fetching doctor profile:', docErr);
          // Fallback to appointment data
          doctorFirstName = appointment.doctor?.firstName || '';
          doctorLastName = appointment.doctor?.lastName || '';
          doctorQualification = appointment.doctor?.qualification || '';
        }
      }
      
      const doctorFullName = `${doctorFirstName} ${doctorLastName}`.trim() || appointment.doctor?.name || appointment.doctorName || '';
      
      // Fetch all prescriptions for this appointment
      const prescriptionsResponse = await prescriptionAPI.getAllPrescriptions({ appointment: appointment._id });
      const prescriptions = prescriptionsResponse?.data?.items || [];
      
      console.log('?? Fetched prescriptions:', prescriptions);
      
      // Extract unique diseases from all prescriptions
      const diseases = [];
      prescriptions.forEach(prescription => {
        if (prescription.knownCaseDisease) {
          const disease = {
            code: prescription.knownCaseDisease.icd10Code || prescription.knownCaseDisease.code || '',
            name: prescription.knownCaseDisease.name || '',
            id: prescription.knownCaseDisease._id || ''
          };
          // Only add if not already in the list
          if (disease.code && !diseases.find(d => d.code === disease.code)) {
            diseases.push(disease);
          }
        }
      });
      
      console.log('?? Extracted diseases:', diseases);
      setAvailableDiseases(diseases);
      
      // Parse dates and times from appointment - Convert UTC to IST
      const startDate = appointment.startDateTime || appointment.appointmentDate || appointment.dateTime;
      const endDate = appointment.endTime || appointment.endDateTime;
      
      const admissionIST = convertUTCtoIST(startDate);
      const dischargeIST = convertUTCtoIST(endDate);
      
      const admissionDate = admissionIST.date;
      const admissionTime = admissionIST.time;
      const dischargeDate = dischargeIST.date || admissionIST.date;
      const dischargeTime = dischargeIST.time;
      
      console.log('?? Parsed dates (IST):', { admissionDate, admissionTime, dischargeDate, dischargeTime });
      
      // Auto-populate first disease if available
      const firstDisease = diseases[0];
      const secondDisease = diseases[1];
      const thirdDisease = diseases[2];
      const fourthDisease = diseases[3];
      
      // Update Form B
      setFormData(prev => ({
        ...prev,
        // Doctor details - use fetched profile data
        treatingDoctor: doctorFullName,
        doctorQualification: doctorQualification,
        // Admission dates and times
        dateOfAdmission: admissionDate,
        timeOfAdmission: admissionTime,
        dateOfDischarge: dischargeDate,
        timeOfDischarge: dischargeTime,
        // Diagnosis from prescriptions
        primaryDiagnosisCode: firstDisease?.code || '',
        primaryDiagnosisDescription: firstDisease?.name || '',
        additionalDiagnosisCode: secondDisease?.code || '',
        additionalDiagnosisDescription: secondDisease?.name || '',
        // Co-morbidities
        comorbidity1Code: thirdDisease?.code || '',
        comorbidity1Description: thirdDisease?.name || '',
        comorbidity2Code: fourthDisease?.code || '',
        comorbidity2Description: fourthDisease?.name || ''
      }));

      // Update Form A
      const formAUpdate = {
        ...formAData,
        // Section D - Hospitalization details
        hospitalName: hospitalDetails?.hospitalName || hospitalDetails?.name || '',
        dateOfAdmission: admissionDate,
        timeOfAdmission: admissionTime,
        dateOfDischarge: dischargeDate,
        timeOfDischarge: dischargeTime,
        hospitalizationDueTo: 'illness', // default value
        injuryOrDiseaseDate: admissionDate,
        diagnosis: firstDisease?.name || '',
        diagnosisIcdCode: firstDisease?.code || '',
        // Clear auto-filled declaration date for Form A
        declarationDate: ''
      };
      console.log('?? Form A appointment update:', formAUpdate);
      setFormAData(formAUpdate);
      
      showToast?.('success', `Appointment data loaded with ${diseases.length} disease(s)`);
    } catch (error) {
      console.error('? Error loading prescriptions:', error);
      showToast?.('warning', 'Loaded appointment but prescriptions not found');
      
      // Still populate basic appointment info
      const startDate = appointment.startDateTime || appointment.appointmentDate || appointment.dateTime;
      const endDate = appointment.endTime || appointment.endDateTime;
      
      const admissionIST = convertUTCtoIST(startDate);
      const dischargeIST = convertUTCtoIST(endDate);
      
      const admissionDate = admissionIST.date;
      const admissionTime = admissionIST.time;
      const dischargeDate = dischargeIST.date || admissionIST.date;
      const dischargeTime = dischargeIST.time;
      
      // Update Form B with basic info
      setFormData(prev => ({
        ...prev,
        treatingDoctor: doctorFullName,
        doctorQualification: doctorQualification,
        dateOfAdmission: admissionDate,
        timeOfAdmission: admissionTime,
        dateOfDischarge: dischargeDate,
        timeOfDischarge: dischargeTime
      }));

      // Update Form A with basic info
      const formAUpdate = {
        ...formAData,
        hospitalName: hospitalDetails?.hospitalName || hospitalDetails?.name || '',
        dateOfAdmission: admissionDate,
        timeOfAdmission: admissionTime,
        dateOfDischarge: dischargeDate,
        timeOfDischarge: dischargeTime,
        hospitalizationDueTo: 'illness',
        injuryOrDiseaseDate: admissionDate,
        // Clear auto-filled declaration date for Form A
        declarationDate: ''
      };
      setFormAData(formAUpdate);
    } finally {
      setLoading(false);
    }
  };

  // Handle form field changes
  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleDocumentChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [field]: value
      }
    }));
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedPatient) {
      showToast?.('error', 'Please select a patient');
      return;
    }

    // Validate patient ID exists
    const patientId = selectedPatient._id || selectedPatient.id;
    if (!patientId) {
      showToast?.('error', 'Invalid patient selected. Please select a patient again.');
      return;
    }

    try {
      setLoading(true);
      
      let savedForm = null;
      
      if (formType === 'B') {
        // Prepare Form B data for submission
        const submissionData = {
          patientId: patientId,
          appointmentId: selectedAppointment?._id,
          hospitalDetails: {
            hospitalName: formData.hospitalName,
            hospitalId: formData.hospitalId,
            treatingDoctor: formData.treatingDoctor,
            doctorQualification: formData.doctorQualification,
            hospitalType: formData.hospitalType,
            stateRegistrationNo: formData.stateRegistrationNo,
            hospitalPhone: formData.hospitalPhone
          },
          patientDetails: {
            patientName: formData.patientName,
            ipRegistrationNumber: formData.ipRegistrationNumber,
            gender: formData.gender,
            age: formData.age,
            ageMonths: formData.ageMonths,
            dateOfBirth: formData.dateOfBirth,
            dateOfAdmission: formData.dateOfAdmission,
            timeOfAdmission: formData.timeOfAdmission,
            dateOfDischarge: formData.dateOfDischarge,
            timeOfDischarge: formData.timeOfDischarge,
            typeOfAdmission: formData.typeOfAdmission,
            dateOfDelivery: formData.dateOfDelivery,
            gravidaStatus: formData.gravidaStatus,
            dischargeStatus: formData.dischargeStatus,
            totalClaimedAmount: formData.totalClaimedAmount
          },
          ailmentDetails: {
            primaryDiagnosis: {
              code: formData.primaryDiagnosisCode,
              description: formData.primaryDiagnosisDescription
            },
            additionalDiagnosis: {
              code: formData.additionalDiagnosisCode,
              description: formData.additionalDiagnosisDescription
            },
            comorbidities: [
              { code: formData.comorbidity1Code, description: formData.comorbidity1Description },
              { code: formData.comorbidity2Code, description: formData.comorbidity2Description }
            ].filter(c => c.code || c.description),
            procedures: [
              { code: formData.procedure1Code, description: formData.procedure1Description },
              { code: formData.procedure2Code, description: formData.procedure2Description },
              { code: formData.procedure3Code, description: formData.procedure3Description }
            ].filter(p => p.code || p.description),
            procedureDetails: formData.procedureDetails,
            preAuthObtained: formData.preAuthObtained,
            preAuthNumber: formData.preAuthNumber,
            authNotObtainedReason: formData.authNotObtainedReason,
            hospitalizationDueToInjury: formData.hospitalizationDueToInjury,
            injuryCause: formData.injuryCause,
            injuryOtherCause: formData.injuryOtherCause,
            substanceAbuseTestConducted: formData.substanceAbuseTestConducted,
            isMedicoLegal: formData.isMedicoLegal,
            reportedToPolice: formData.reportedToPolice,
            policeNotReportedReason: formData.policeNotReportedReason,
            firNumber: formData.firNumber
          },
          documents: formData.documents,
          nonNetworkDetails: formData.hospitalType === 'non-network' ? {
            address: formData.nonNetworkHospitalAddress,
            city: formData.nonNetworkHospitalCity,
            state: formData.nonNetworkHospitalState,
            pinCode: formData.nonNetworkHospitalPinCode,
            phone: formData.nonNetworkHospitalPhone,
            registrationNo: formData.nonNetworkHospitalRegistrationNo,
            pan: formData.nonNetworkHospitalPan,
            inpatientBeds: formData.nonNetworkInpatientBeds,
            hasFacilityOT: formData.nonNetworkHasFacilityOT,
            hasFacilityICU: formData.nonNetworkHasFacilityICU,
            otherFacilities: formData.nonNetworkOtherFacilities
          } : undefined,
          hospitalDeclaration: {
            declarationDate: formData.declarationDate,
            declarationPlace: formData.declarationPlace
          }
        };

        console.log('Form B Submission Data:', submissionData);
        console.log('Patient ID being sent:', patientId);
        
        // Save the form to backend
        const response = await claimFormBAPI.createForm(submissionData);
        savedForm = response.data;
        
        showToast?.('success', 'Claim Form B saved successfully');
        
        // Generate and offer to download PDF
        try {
          console.log('Generating PDF with saved form data...');
          
          // Get today's date and hospital city for declaration
          const today = new Date();
          const formattedDate = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;
          
          // Get hospital city reliably
          const hospitalCity = await getDeclarationCity();
          
          // Add declaration data to saved form before PDF generation
          const pdfData = {
            ...savedForm,
            declarationPlace: hospitalCity,
            declarationDate: formattedDate,
            hospitalDeclaration: {
              ...savedForm.hospitalDeclaration,
              declarationDate: formattedDate,
              declarationPlace: hospitalCity
            }
          };
          
          const pdfBytes = await generateClaimFormBPdf(pdfData);
          
          // Ask user if they want to download the PDF
          const shouldDownload = window.confirm('Form saved successfully! Would you like to download the PDF now?');
          if (shouldDownload) {
            const patientName = formData.patientName || 'Patient';
            const filename = `ClaimFormB_${patientName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.pdf`;
            
            // Create blob and download
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            showToast?.('success', 'PDF downloaded successfully');
          }
        } catch (pdfError) {
          console.error('Error generating PDF:', pdfError);
          showToast?.('warning', 'Form saved but PDF generation failed. You can download it from the saved forms list.');
        }
      } else {
        // Prepare Form A data for submission
        const submissionData = {
          patient: patientId,
          appointment: selectedAppointment?._id,
          primaryInsured: {
            policyNo: formAData.policyNo,
            certificateNo: formAData.certificateNo,
            companyTpaId: formAData.companyTpaId,
            name: formAData.insuredName,
            address: formAData.insuredAddress,
            city: formAData.insuredCity,
            state: formAData.insuredState,
            pinCode: formAData.insuredPinCode,
            phone: formAData.insuredPhone,
            email: formAData.insuredEmail
          },
          insuranceHistory: {
            coveredByOtherInsurance: formAData.coveredByOtherInsurance,
            firstInsuranceCommencementDate: formAData.firstInsuranceCommencementDate,
            otherCompanyName: formAData.otherCompanyName,
            otherPolicyNo: formAData.otherPolicyNo,
            sumInsured: formAData.sumInsured,
            hospitalizedInLastFourYears: formAData.hospitalizedInLastFourYears,
            lastHospitalizationDate: formAData.lastHospitalizationDate,
            lastHospitalizationDiagnosis: formAData.lastHospitalizationDiagnosis,
            previouslyCovered: formAData.previouslyCovered,
            previousCompanyName: formAData.previousCompanyName
          },
          patientDetails: {
            name: formAData.patientName,
            gender: formAData.patientGender,
            age: formAData.patientAge,
            ageMonths: formAData.patientAgeMonths,
            dob: formAData.patientDob,
            relationshipToPrimary: formAData.relationshipToPrimary || 'self',
            relationshipOther: formAData.relationshipOther,
            // Only include occupation if it has a value (enum field)
            ...(formAData.patientOccupation && { occupation: formAData.patientOccupation }),
            occupationOther: formAData.patientOccupationOther,
            address: formAData.patientAddress,
            city: formAData.patientCity,
            state: formAData.patientState,
            pinCode: formAData.patientPinCode,
            phone: formAData.patientPhone,
            email: formAData.patientEmail
          },
          hospitalizationDetails: {
            hospitalName: formAData.hospitalName,
            roomCategory: formAData.roomCategory,
            hospitalizationDueTo: formAData.hospitalizationDueTo || 'illness',
            injuryOrDiseaseDate: formAData.injuryOrDiseaseDate,
            dateOfDelivery: formAData.dateOfDelivery,
            dateOfAdmission: formAData.dateOfAdmission,
            timeOfAdmission: formAData.timeOfAdmission,
            dateOfDischarge: formAData.dateOfDischarge,
            timeOfDischarge: formAData.timeOfDischarge,
            // Only include injuryCause if it has a value (enum field)
            ...(formAData.injuryCause && { injuryCause: formAData.injuryCause }),
            isMedicoLegal: formAData.isMedicoLegal,
            reportedToPolice: formAData.reportedToPolice,
            mlcReportAttached: formAData.mlcReportAttached,
            systemOfMedicine: formAData.systemOfMedicine,
            diagnosis: formAData.diagnosis,
            diagnosisIcdCode: formAData.diagnosisIcdCode
          },
          claimDetails: {
            preHospitalizationExpenses: formAData.preHospitalizationExpenses,
            hospitalizationExpenses: formAData.hospitalizationExpenses,
            postHospitalizationExpenses: formAData.postHospitalizationExpenses,
            healthCheckupCost: formAData.healthCheckupCost,
            ambulanceCharges: formAData.ambulanceCharges,
            otherExpenses: formAData.otherExpenses,
            otherExpensesCode: formAData.otherExpensesCode,
            totalClaimAmount: formAData.totalClaimAmount,
            preHospitalizationPeriod: formAData.preHospitalizationPeriod,
            postHospitalizationPeriod: formAData.postHospitalizationPeriod,
            claimForDomiciliary: formAData.claimForDomiciliary,
            domiciliaryDetails: formAData.domiciliaryDetails,
            lumpSumBenefits: {
              hospitalDailyCash: formAData.hospitalDailyCash,
              criticalIllnessBenefit: formAData.criticalIllnessBenefit,
              surgicalCash: formAData.surgicalCash,
              convalescence: formAData.convalescence,
              prePostHospitalizationLumpSum: formAData.prePostHospitalizationLumpSum,
              others: formAData.othersLumpSum,
              total: formAData.totalLumpSum
            }
          },
          documents: formAData.documents,
          billsEnclosed: formAData.bills.filter(bill => bill.billNo || bill.date || bill.issuedBy || bill.towards || bill.amount),
          bankAccount: {
            pan: formAData.pan,
            accountNumber: formAData.accountNumber,
            bankName: formAData.bankName,
            bankBranch: formAData.bankBranch,
            chequePayableDetails: formAData.chequePayableDetails,
            ifscCode: formAData.ifscCode
          },
          declaration: {
            // Convert DD/MM/YYYY to ISO date format, or omit if empty
            ...(formAData.declarationDate && {
              date: (() => {
                try {
                  const parts = formAData.declarationDate.split('/');
                  if (parts.length === 3) {
                    // DD/MM/YYYY format
                    return new Date(`${parts[2]}-${parts[1]}-${parts[0]}`).toISOString();
                  }
                  // Try to parse as-is
                  return new Date(formAData.declarationDate).toISOString();
                } catch (e) {
                  return undefined;
                }
              })()
            }),
            place: formAData.declarationPlace,
            signature: formAData.declarationSignature
          }
        };

        console.log('Form A Submission Data:', submissionData);
        console.log('Patient ID being sent:', patientId);
        
        // Save the form to backend
        const response = await claimFormAAPI.createForm(submissionData);
        savedForm = response.data;
        
        showToast?.('success', 'Claim Form A saved successfully');
        
        // Generate and offer to download PDF for Form A
        try {
          console.log('Generating Form A PDF with saved form data...');
          const pdfBytes = await generateClaimFormAPdf(savedForm);
          
          // Ask user if they want to download the PDF
          const shouldDownload = window.confirm('Form saved successfully! Would you like to download the PDF now?');
          if (shouldDownload) {
            const patientName = formAData.patientName || 'Patient';
            const filename = `ClaimFormA_${patientName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.pdf`;
            
            // Create blob and download
            const blob = new Blob([pdfBytes], { type: 'application/pdf' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            showToast?.('success', 'PDF downloaded successfully');
          }
        } catch (pdfError) {
          console.error('Error generating Form A PDF:', pdfError);
          showToast?.('warning', 'Form saved but PDF generation failed. You can download it from the saved forms list.');
        }
      }
      
      // Refresh saved forms list and switch to saved tab
      loadSavedForms();
      setActiveTab('saved');
      
    } catch (error) {
      console.error(`Error submitting Form ${formType}:`, error);
      
      // Enhanced error handling for patient validation
      if (error.response?.status === 404 && error.response?.data?.message === 'Patient not found') {
        showToast?.('error', 'Patient not found. Please select the patient again from the search results.');
        setSelectedPatient(null);
        setPatientSearchResults([]);
        setPatientSearch('');
      } else if (error.response?.status === 400) {
        showToast?.('error', error.response?.data?.message || 'Invalid data provided. Please check all fields.');
      } else {
        showToast?.('error', error.response?.data?.message || `Failed to save Claim Form ${formType}`);
      }
    } finally {
      setLoading(false);
    }
  };

  // Load saved forms
  // Load saved forms based on selected form type
  const loadSavedForms = async () => {
    try {
      setLoadingSavedForms(true);
      const params = {
        page: savedFormsPage,
        limit: 20,
        patientSearch: savedFormsSearch || undefined
      };
      
      let forms = [];
      let pagination = {};

      if (formType === 'A') {
        const response = await claimFormAAPI.getAllForms(params);
        forms = (response.data || []).map(form => ({ ...form, formType: 'A' }));
        pagination = response.pagination;
      } else {
        const response = await claimFormBAPI.getAllForms(params);
        forms = (response.data || []).map(form => ({ ...form, formType: 'B' }));
        pagination = response.pagination;
      }
      
      setSavedForms(forms);
      setSavedFormsPagination(pagination);
      
    } catch (error) {
      console.error(`Error loading saved forms (${formType}):`, error);
      setSavedForms([]);
    } finally {
      setLoadingSavedForms(false);
    }
  };

  // Delete a saved form
  const handleDeleteForm = async (formId, formType = 'B') => {
    setDeleteModal({ show: true, formId, formType });
  };

  const confirmDelete = async () => {
    const { formId, formType } = deleteModal;
    try {
      if (formType === 'A') {
        await claimFormAAPI.deleteForm(formId);
      } else {
        await claimFormBAPI.deleteForm(formId);
      }
      showToast?.('success', 'Form deleted successfully');
      loadSavedForms();
    } catch (error) {
      console.error('Error deleting form:', error);
      showToast?.('error', error.response?.data?.message || 'Failed to delete form');
    } finally {
      setDeleteModal({ show: false, formId: null, formType: null });
    }
  };

  // Download PDF for a saved form
  const handleDownloadSavedFormPdf = async (form) => {
    setDownloadModal({ show: true, form });
  };

  const confirmDownload = async () => {
    const form = downloadModal.form;
    try {
      setLoading(true);
      console.log('?? Downloading PDF for saved form:', form._id);
      
      let pdfBytes = null;
      const patientName = form.patientDetails?.patientName || form.patientDetails?.name || 'Patient';
      
      if (form.formType === 'B' || (!form.formType && form.hospitalDetails)) {
        // Get today's date and hospital city for declaration
        const today = new Date();
        const formattedDate = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;
        
        // Ensure we have a city, fetching if necessary
        const hospitalCity = await getDeclarationCity();
        console.log('?? Saving SavedForm PDF - Using City:', hospitalCity);
        
        // Add declaration data to form before PDF generation
        const pdfData = {
          ...form,
          declarationPlace: hospitalCity,
          declarationDate: formattedDate,
          hospitalDeclaration: {
            ...form.hospitalDeclaration,
            declarationDate: formattedDate,
            declarationPlace: hospitalCity
          }
        };
        
        // Form B PDF generation
        pdfBytes = await generateClaimFormBPdf(pdfData);
      } else {
        // Form A PDF generation
        pdfBytes = await generateClaimFormAPdf(form);
      }
      
      if (pdfBytes) {
        const filename = `ClaimForm${form.formType || 'B'}_${patientName.replace(/\s+/g, '_')}_${new Date(form.createdAt || Date.now()).toISOString().slice(0,10)}.pdf`;
        
        // Create blob and download
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        showToast?.('success', '?? PDF downloaded successfully');
      }
    } catch (error) {
      console.error('? Error downloading PDF:', error);
      showToast?.('error', 'Failed to download PDF. Please try again.');
    } finally {
      setLoading(false);
      setDownloadModal({ show: false, form: null });
    }
  };

  // Submit a saved form
  const handleSubmitSavedForm = async (formId) => {
    if (!window.confirm('Are you sure you want to submit this form? Once submitted, it cannot be edited.')) {
      return;
    }

    try {
      await claimFormBAPI.submitForm(formId);
      showToast?.('success', 'Form submitted successfully');
      loadSavedForms();
    } catch (error) {
      console.error('Error submitting form:', error);
      showToast?.('error', error.response?.data?.message || 'Failed to submit form');
    }
  };

  // View a saved form
  const handleViewForm = async (formId, formType = 'B') => {
    try {
      const response = formType === 'A' 
        ? await claimFormAAPI.getFormById(formId)
        : await claimFormBAPI.getFormById(formId);
      setViewingForm({ ...response.data, formType });
      // Scroll to top to show the form
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error loading form:', error);
    }
  };

  // Print a saved form using the HTML template
  const handlePrintForm = async (form) => {
    // Use appropriate HTML template based on form type
    if (form.formType === 'A') {
      const htmlContent = generateClaimFormAHtml(form);
      const printWindow = window.open('', '_blank', 'width=1200,height=800');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 500);
      }
    } else {
      // For Form B (default) - Use new PDF generation
      // Get today's date in DD/MM/YYYY format
      const today = new Date();
      const formattedDate = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;
      
      // Get hospital city reliably
      const hospitalCity = await getDeclarationCity();
      
      console.log('??? handlePrintForm - hospitalCity:', hospitalCity);
      
      // Merge declaration data at print time
      const printData = {
        ...form,
        // Add hospital city directly at root level for transform to find
        declarationPlace: hospitalCity,
        declarationDate: formattedDate,
        hospitalDeclaration: {
          ...form.hospitalDeclaration,
          declarationDate: form.hospitalDeclaration?.declarationDate || formattedDate,
          declarationPlace: hospitalCity // Force overwrite with latest city
        }
      };
      
      console.log('??? handlePrintForm - printData.hospitalDeclaration:', printData.hospitalDeclaration);
      
      generateClaimFormBPdf(printData)
        .then(pdfBytes => {
          const blob = new Blob([pdfBytes], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          const printWindow = window.open(url, '_blank');
          if (printWindow) {
            printWindow.onload = () => {
              printWindow.print();
            };
          }
          // Clean up URL after some time
          setTimeout(() => URL.revokeObjectURL(url), 5000);
        })
        .catch(error => {
          console.error('Error generating PDF for print:', error);
          showToast('Error generating PDF for printing', 'error');
        });
    }
  };

  // Download form as PDF directly using iframe print
  const handleDownloadForm = async (form) => {
    // Use appropriate PDF generator based on form type
    if (form.formType === 'A') {
      // Generate patient name for filename
      const patientName = form.patientName || 'Patient';
      const filename = `ClaimFormA_${patientName.replace(/\s+/g, '_')}.pdf`;
      downloadClaimFormAPdf(form, filename);
    } else {
      // For Form B (default)
      try {
        const patientName = form.patientDetails?.patientName || form.patientName || 'Patient';
        const filename = `ClaimFormB_${patientName.replace(/\s+/g, '_')}.pdf`;

        // Get fresh declaration city
        const today = new Date();
        const formattedDate = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;
        const hospitalCity = await getDeclarationCity(); // Ensure we have city

        const pdfData = {
          ...form,
          declarationPlace: hospitalCity,
          declarationDate: formattedDate,
          hospitalDeclaration: {
            ...form.hospitalDeclaration,
            declarationDate: form.hospitalDeclaration?.declarationDate || formattedDate,
            declarationPlace: hospitalCity // Force overwrite
          }
        };

        console.log('?? Download Clean PDF - Using City:', hospitalCity);
        await downloadClaimFormBPdf(pdfData, filename);
      } catch(e) {
        console.error("Error downloading PDF", e);
        showToast('Error downloading PDF', 'error');
      }
    }
  };

  // Close viewing form
  const handleCloseView = () => {
    setViewingForm(null);
  };

  // Load saved forms when switching to saved tab, changing page, or changing form type
  useEffect(() => {
    if (activeTab === 'saved') {
      loadSavedForms();
    }
  }, [activeTab, savedFormsPage, formType]);

  // Debounced search effect
  useEffect(() => {
    if (activeTab !== 'saved') return;
    
    const timeoutId = setTimeout(() => {
      // Only reset page and load if we actually have a search change
      if (savedFormsPage === 1) {
        loadSavedForms();
      } else {
        setSavedFormsPage(1); // This will trigger the above effect
      }
    }, 500); // Wait 500ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [savedFormsSearch]);

  // Print form - for new forms using current form data
  const handlePrint = async () => {
    if (formType === 'B') {
      try {
        // Get today's date in DD/MM/YYYY format
        const today = new Date();
        const formattedDate = `${String(today.getDate()).padStart(2, '0')}/${String(today.getMonth() + 1).padStart(2, '0')}/${today.getFullYear()}`;
        
        // Get hospital city reliably
        const hospitalCity = await getDeclarationCity();
        
        console.log('??? handlePrint (New Form) - City:', hospitalCity);
        
        // Merge declaration data at print time to ensure it's populated
        const printData = {
          ...formData,
          declarationDate: formData.declarationDate || formattedDate,
          declarationPlace: formData.declarationPlace || hospitalCity,
          hospitalDeclaration: {
            declarationDate: formData.declarationDate || formattedDate,
            declarationPlace: formData.declarationPlace || hospitalCity
          }
        };
        
        console.log('??? Print data with declaration:', { 
          declarationDate: printData.declarationDate, 
          declarationPlace: printData.declarationPlace,
          hospitalCity 
        });
        
        // Use new PDF generation for Form B with current form data
        const pdfBytes = await generateClaimFormBPdf(printData);
        const blob = new Blob([pdfBytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const printWindow = window.open(url, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
          };
        }
        // Clean up URL after some time
        setTimeout(() => URL.revokeObjectURL(url), 5000);
      } catch (error) {
        console.error('Error generating PDF for print:', error);
        showToast('Error generating PDF for printing', 'error');
      }
    } else {
      // Use the HTML template for Form A with current form data
      const htmlContent = generateClaimFormAHtml(formAData);
      const printWindow = window.open('', '_blank', 'width=1200,height=800');
      if (printWindow) {
        printWindow.document.write(htmlContent);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
        }, 500);
      }
    }
  };

  return (
    <div className="min-h-screen bg-white p-8" style={{ fontFamily: 'Poppins' }}>
      <div className="max-w-7xl mx-auto">
        {/* Delete Confirmation Modal */}
        {deleteModal.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-center w-14 h-14 mx-auto bg-red-100 rounded-full mb-4">
                  <AlertCircle className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 text-center mb-2">Delete Form</h3>
                <p className="text-lg text-gray-600 text-center mb-6 font-medium">
                  Are you sure you want to delete this Form {deleteModal.formType}? This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setDeleteModal({ show: false, formId: null, formType: null })}
                    className="flex-1 px-5 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium text-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDelete}
                    className="flex-1 px-5 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium text-lg"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Download Confirmation Modal */}
        {downloadModal.show && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-center w-14 h-14 mx-auto bg-green-100 rounded-full mb-4">
                  <Download className="w-7 h-7 text-green-600" />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900 text-center mb-2">Download PDF</h3>
                <p className="text-lg text-gray-600 text-center mb-3 font-medium">
                  Ready to download the claim form as PDF
                </p>
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <div className="text-base text-gray-700">
                    <p><span className="font-semibold">Patient:</span> {downloadModal.form?.patientDetails?.patientName || downloadModal.form?.patientDetails?.name}</p>
                    <p><span className="font-semibold">Form Type:</span> Form {downloadModal.form?.formType || 'B'}</p>
                    <p><span className="font-semibold">Amount:</span> ?{(downloadModal.form?.patientDetails?.totalClaimedAmount || downloadModal.form?.claimDetails?.totalClaimAmount || 0).toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setDownloadModal({ show: false, form: null })}
                    className="flex-1 px-5 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium text-lg"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDownload}
                    disabled={loading}
                    className="flex-1 px-5 py-3 bg-[#2D869C] text-white rounded-xl hover:bg-[#2D869C]/90 transition-colors font-medium disabled:opacity-50 flex items-center justify-center text-lg shadow-sm hover:shadow-md"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                        Downloading...
                      </>
                    ) : (
                      <>
                        <Download className="w-5 h-5 mr-2" />
                        Download
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Form Modal */}
        {viewingForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-start justify-center overflow-y-auto p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full my-8">
              {/* Modal Header */}
              <div className="sticky top-0 bg-[#2D869C] px-6 py-4 flex justify-between items-center rounded-t-lg z-10 shadow-md">
                <div className="flex items-center gap-3">
                  <FileText className="w-6 h-6 text-white" />
                  <div>
                    <h2 className="text-3xl font-bold text-white">
                      Claim Form {viewingForm.formType || 'B'} {viewingForm.formType === 'A' ? '' : ''}
                    </h2>
                    <p className="text-base font-medium text-white/90">
                      Patient: {viewingForm.patientDetails?.patientName || viewingForm.patientDetails?.name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseView}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="px-6 py-6 space-y-5 max-h-[calc(100vh-220px)] overflow-y-auto bg-gray-50 text-base">
                {viewingForm.formType === 'A' ? (
                  // Form A - Reimbursement Claim View
                  <div className="space-y-5">
                    {/* Primary Insured */}
                    <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-[#2D869C]">
                      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <Shield className="w-6 h-6 mr-2 text-[#2D869C]" />
                        Primary Insured Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Policy Number</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.primaryInsured?.policyNo || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Certificate Number</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.primaryInsured?.certificateNo || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Company/TPA ID</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.primaryInsured?.companyTpaId || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Name</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.primaryInsured?.name || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg md:col-span-2">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Address</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.primaryInsured?.address || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">City</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.primaryInsured?.city || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">State</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.primaryInsured?.state || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Pin Code</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.primaryInsured?.pinCode || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Phone</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.primaryInsured?.phone || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg md:col-span-2">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Email</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.primaryInsured?.email || '-'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Insurance History */}
                    <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-[#2D869C]">
                      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <History className="w-6 h-6 mr-2 text-[#2D869C]" />
                        Insurance History
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">First Insurance Date</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.insuranceHistory?.firstInsuranceCommencementDate ? new Date(viewingForm.insuranceHistory.firstInsuranceCommencementDate).toLocaleDateString() : '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Sum Insured</span>
                          <p className="text-base font-semibold text-green-600">?{viewingForm.insuranceHistory?.sumInsured?.toLocaleString() || '0'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Covered by Other Insurance</span>
                          <p className="text-base font-semibold text-gray-900 capitalize">{viewingForm.insuranceHistory?.coveredByOtherInsurance || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Hospitalized in Last 4 Years</span>
                          <p className="text-base font-semibold text-gray-900 capitalize">{viewingForm.insuranceHistory?.hospitalizedInLastFourYears || '-'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Patient Details */}
                    <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-[#2D869C]">
                      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <User className="w-6 h-6 mr-2 text-[#2D869C]" />
                        Patient Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Name</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.patientDetails?.name || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Gender</span>
                          <p className="text-base font-semibold text-gray-900 capitalize">{viewingForm.patientDetails?.gender || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Age</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.patientDetails?.age || '0'} years {viewingForm.patientDetails?.ageMonths || '0'} months</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Date of Birth</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.patientDetails?.dob ? new Date(viewingForm.patientDetails.dob).toLocaleDateString() : '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Occupation</span>
                          <p className="text-base font-semibold text-gray-900 capitalize">{viewingForm.patientDetails?.occupation || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Relationship to Primary</span>
                          <p className="text-base font-semibold text-gray-900 capitalize">{viewingForm.patientDetails?.relationshipToPrimary || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg md:col-span-2">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Address</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.patientDetails?.address || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Phone</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.patientDetails?.phone || '-'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Hospitalization Details */}
                    <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-[#2D869C]">
                      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <Building2 className="w-6 h-6 mr-2 text-[#2D869C]" />
                        Hospitalization Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-gray-50 p-3 rounded-lg md:col-span-2">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Hospital</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.hospitalizationDetails?.hospitalName || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Room Category</span>
                          <p className="text-base font-semibold text-gray-900 capitalize">{viewingForm.hospitalizationDetails?.roomCategory?.replace(/_/g, ' ') || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Hospitalization Reason</span>
                          <p className="text-base font-semibold text-gray-900 capitalize">{viewingForm.hospitalizationDetails?.hospitalizationDueTo || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Admission</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.hospitalizationDetails?.dateOfAdmission ? new Date(viewingForm.hospitalizationDetails.dateOfAdmission).toLocaleDateString() : '-'} {viewingForm.hospitalizationDetails?.timeOfAdmission || ''}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Discharge</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.hospitalizationDetails?.dateOfDischarge ? new Date(viewingForm.hospitalizationDetails.dateOfDischarge).toLocaleDateString() : '-'} {viewingForm.hospitalizationDetails?.timeOfDischarge || ''}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Injury Cause</span>
                          <p className="text-base font-semibold text-gray-900 capitalize">{viewingForm.hospitalizationDetails?.injuryCause?.replace(/_/g, ' ') || 'N/A'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Medico Legal</span>
                          <p className="text-base font-semibold text-gray-900 capitalize">{viewingForm.hospitalizationDetails?.isMedicoLegal || '-'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Claim Details */}
                    <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-green-500">
                      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <FileText className="w-6 h-6 mr-2 text-green-600" />
                        Claim Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Pre-Hospitalization</span>
                          <p className="text-base font-semibold text-gray-900">?{viewingForm.claimDetails?.preHospitalizationExpenses?.toLocaleString() || '0'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Hospitalization</span>
                          <p className="text-base font-semibold text-gray-900">?{viewingForm.claimDetails?.hospitalizationExpenses?.toLocaleString() || '0'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Post-Hospitalization</span>
                          <p className="text-base font-semibold text-gray-900">?{viewingForm.claimDetails?.postHospitalizationExpenses?.toLocaleString() || '0'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Ambulance</span>
                          <p className="text-base font-semibold text-gray-900">?{viewingForm.claimDetails?.ambulanceCharges?.toLocaleString() || '0'}</p>
                        </div>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg border-2 border-green-200">
                        <span className="text-base font-semibold text-green-700 block mb-1">Total Claim Amount</span>
                        <p className="text-3xl font-bold text-green-600">?{viewingForm.claimDetails?.totalClaimAmount?.toLocaleString() || '0'}</p>
                      </div>
                    </div>

                    {/* Bills Enclosed */}
                    {viewingForm.billsEnclosed?.length > 0 && (
                      <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-[#2D869C]">
                        <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                          <Receipt className="w-6 h-6 mr-2 text-[#2D869C]" />
                          Bills Enclosed
                        </h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-base">
                            <thead className="bg-gray-50">
                              <tr className="border-b-2 border-gray-200">
                                <th className="text-left py-3 px-3 font-bold text-gray-700">Bill No</th>
                                <th className="text-left py-3 px-3 font-bold text-gray-700">Date</th>
                                <th className="text-left py-3 px-3 font-bold text-gray-700">Issued By</th>
                                <th className="text-left py-3 px-3 font-bold text-gray-700">Towards</th>
                                <th className="text-right py-3 px-3 font-bold text-gray-700">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {viewingForm.billsEnclosed.map((bill, i) => (
                                <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                                  <td className="py-3 px-3 font-medium text-gray-900">{bill.billNo}</td>
                                  <td className="py-3 px-3 font-medium text-gray-900">{new Date(bill.date).toLocaleDateString()}</td>
                                  <td className="py-3 px-3 font-medium text-gray-900">{bill.issuedBy}</td>
                                  <td className="py-3 px-3 font-medium text-gray-900">{bill.towards}</td>
                                  <td className="py-3 px-3 text-right text-gray-900 font-bold">?{bill.amount}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Bank Details */}
                    <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-[#2D869C]">
                      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <CreditCard className="w-6 h-6 mr-2 text-[#2D869C]" />
                        Bank Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Account Number</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.bankAccount?.accountNumber || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Bank Name</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.bankAccount?.bankName || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">Branch</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.bankAccount?.bankBranch || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">IFSC Code</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.bankAccount?.ifscCode || '-'}</p>
                        </div>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <span className="text-md font-semibold text-gray-600 block mb-1">PAN</span>
                          <p className="text-base font-semibold text-gray-900">{viewingForm.bankAccount?.pan || '-'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Documents */}
                    <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-[#2D869C]">
                      <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                        <CheckSquare className="w-6 h-6 mr-2 text-[#2D869C]" />
                        Documents Submitted
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(viewingForm.documents || {}).map(([key, value]) => {
                          if (key === 'othersSpecify' || typeof value !== 'boolean') return null;
                          return value && (
                            <div key={key} className="flex items-center bg-green-50 p-3 rounded-lg border border-green-200">
                              <CheckSquare className="w-5 h-5 mr-2 text-green-600 flex-shrink-0" />
                              <span className="text-base font-semibold text-gray-700">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ) : (
                  // Form B - Cashless Claim View
                  <div className="space-y-5">
                {/* Hospital Details */}
                <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-[#2D869C]">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                    <Building2 className="w-6 h-6 mr-2 text-[#2D869C]" />
                    Hospital Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-md font-semibold text-gray-600 block mb-1">Hospital Name</span>
                      <p className="text-base font-semibold text-gray-900">{viewingForm.hospitalDetails?.hospitalName || '-'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-md font-semibold text-gray-600 block mb-1">Hospital ID</span>
                      <p className="text-base font-semibold text-gray-900">{viewingForm.hospitalDetails?.hospitalId || '-'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-md font-semibold text-gray-600 block mb-1">Treating Doctor</span>
                      <p className="text-base font-semibold text-gray-900">{viewingForm.hospitalDetails?.treatingDoctor || '-'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-md font-semibold text-gray-600 block mb-1">Qualification</span>
                      <p className="text-base font-semibold text-gray-900">{viewingForm.hospitalDetails?.doctorQualification || '-'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-md font-semibold text-gray-600 block mb-1">Hospital Type</span>
                      <p className="text-base font-semibold text-gray-900 capitalize">{viewingForm.hospitalDetails?.hospitalType || '-'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-md font-semibold text-gray-600 block mb-1">Phone</span>
                      <p className="text-base font-semibold text-gray-900">{viewingForm.hospitalDetails?.hospitalPhone || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Patient Details */}
                <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-[#2D869C]">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                    <User className="w-6 h-6 mr-2 text-[#2D869C]" />
                    Patient Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-md font-semibold text-gray-500 block mb-1">Patient Name</span>
                      <p className="font-semibold text-gray-900">{viewingForm.patientDetails?.patientName || '-'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-md font-semibold text-gray-500 block mb-1">IP Number</span>
                      <p className="font-semibold text-gray-900">{viewingForm.patientDetails?.ipRegistrationNumber || '-'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-md font-semibold text-gray-500 block mb-1">Gender</span>
                      <p className="font-semibold text-gray-900 capitalize">{viewingForm.patientDetails?.gender || '-'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-md font-semibold text-gray-500 block mb-1">Age</span>
                      <p className="font-semibold text-gray-900">{viewingForm.patientDetails?.age || '0'} years {viewingForm.patientDetails?.ageMonths || '0'} months</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-md font-semibold text-gray-500 block mb-1">Date of Birth</span>
                      <p className="font-semibold text-gray-900">{viewingForm.patientDetails?.dateOfBirth ? new Date(viewingForm.patientDetails.dateOfBirth).toLocaleDateString() : '-'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-md font-semibold text-gray-500 block mb-1">Admission</span>
                      <p className="font-semibold text-gray-900">{viewingForm.patientDetails?.dateOfAdmission ? new Date(viewingForm.patientDetails.dateOfAdmission).toLocaleDateString() : '-'} {viewingForm.patientDetails?.timeOfAdmission || ''}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-md font-semibold text-gray-500 block mb-1">Discharge</span>
                      <p className="font-semibold text-gray-900">{viewingForm.patientDetails?.dateOfDischarge ? new Date(viewingForm.patientDetails.dateOfDischarge).toLocaleDateString() : '-'} {viewingForm.patientDetails?.timeOfDischarge || ''}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-md font-semibold text-gray-500 block mb-1">Admission Type</span>
                      <p className="font-semibold text-gray-900 capitalize">{viewingForm.patientDetails?.typeOfAdmission || '-'}</p>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg">
                      <span className="text-md font-semibold text-gray-500 block mb-1">Discharge Status</span>
                      <p className="font-semibold text-gray-900 capitalize">{viewingForm.patientDetails?.dischargeStatus?.replace('_', ' ') || '-'}</p>
                    </div>
                    <div className="bg-green-50 p-3 rounded-lg border-2 border-green-200 md:col-span-2 lg:col-span-3">
                      <span className="text-xs text-green-700 block mb-1">Total Claimed Amount</span>
                      <p className="font-bold text-xl text-green-600">?{viewingForm.patientDetails?.totalClaimedAmount?.toLocaleString() || '0'}</p>
                    </div>
                  </div>
                </div>

                {/* Ailment Details */}
                <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-[#2D869C]">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                    <FileText className="w-6 h-6 mr-2 text-[#2D869C]" />
                    Ailment Details
                  </h3>
                  <div className="space-y-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <span className="text-base font-bold text-gray-700 block mb-2">Primary Diagnosis</span>
                      <p className="text-base"><span className="font-bold text-[#2D869C]">{viewingForm.ailmentDetails?.primaryDiagnosis?.code}</span> - {viewingForm.ailmentDetails?.primaryDiagnosis?.description}</p>
                    </div>
                    {viewingForm.ailmentDetails?.additionalDiagnosis?.code && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <span className="text-base font-bold text-gray-700 block mb-2">Additional Diagnosis</span>
                        <p className="text-base"><span className="font-bold text-[#2D869C]">{viewingForm.ailmentDetails?.additionalDiagnosis?.code}</span> - {viewingForm.ailmentDetails?.additionalDiagnosis?.description}</p>
                      </div>
                    )}
                    {viewingForm.ailmentDetails?.comorbidities?.length > 0 && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <span className="text-base font-bold text-gray-700 block mb-2">Comorbidities</span>
                        <ul className="space-y-1">
                          {viewingForm.ailmentDetails.comorbidities.map((com, idx) => (
                            <li key={idx} className="text-base flex items-start">
                              <span className="text-[#2D869C] mr-2">�</span>
                              <span><span className="font-bold">{com.code}</span> - {com.description}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {viewingForm.ailmentDetails?.procedures?.length > 0 && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <span className="text-base font-bold text-gray-700 block mb-2">Procedures</span>
                        <ul className="space-y-1">
                          {viewingForm.ailmentDetails.procedures.map((proc, idx) => (
                            <li key={idx} className="text-base flex items-start">
                              <span className="text-[#2D869C] mr-2">�</span>
                              <span><span className="font-bold">{proc.code}</span> - {proc.description}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {viewingForm.ailmentDetails?.procedureDetails && (
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <span className="text-base font-bold text-gray-700 block mb-2">Procedure Details</span>
                        <p className="text-base text-gray-900 font-medium">{viewingForm.ailmentDetails.procedureDetails}</p>
                      </div>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <span className="text-md font-semibold text-gray-600 block mb-1">Pre-authorization</span>
                        <p className="text-base font-semibold text-gray-900">{viewingForm.ailmentDetails?.preAuthObtained === 'yes' ? 'Yes' : 'No'}</p>
                        {viewingForm.ailmentDetails?.preAuthObtained === 'yes' && viewingForm.ailmentDetails?.preAuthNumber && (
                          <p className="text-base font-medium text-gray-600 mt-1">Number: {viewingForm.ailmentDetails.preAuthNumber}</p>
                        )}
                        {viewingForm.ailmentDetails?.preAuthObtained === 'no' && viewingForm.ailmentDetails?.authNotObtainedReason && (
                          <p className="text-base font-medium text-gray-600 mt-1">Reason: {viewingForm.ailmentDetails.authNotObtainedReason}</p>
                        )}
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <span className="text-md font-semibold text-gray-600 block mb-1">Hospitalization due to Injury</span>
                        <p className="text-base font-semibold text-gray-900">{viewingForm.ailmentDetails?.hospitalizationDueToInjury === 'yes' ? 'Yes' : 'No'}</p>
                        {viewingForm.ailmentDetails?.hospitalizationDueToInjury === 'yes' && viewingForm.ailmentDetails?.injuryCause && (
                          <p className="text-base font-medium text-gray-600 mt-1 capitalize">Cause: {viewingForm.ailmentDetails.injuryCause.replace(/_/g, ' ')}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Injury-related details - shown only if hospitalization due to injury */}
                    {viewingForm.ailmentDetails?.hospitalizationDueToInjury === 'yes' && (
                      <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {viewingForm.ailmentDetails?.injuryCause === 'substance_abuse' && (
                            <div className="bg-white p-3 rounded-lg">
                              <span className="text-md font-semibold text-gray-600 block mb-1">Substance Abuse Test Conducted</span>
                              <p className="text-base font-semibold text-gray-900">{viewingForm.ailmentDetails?.substanceAbuseTestConducted === 'yes' ? 'Yes' : 'No'}</p>
                            </div>
                          )}
                          <div className="bg-white p-3 rounded-lg">
                            <span className="text-md font-semibold text-gray-600 block mb-1">Medico Legal</span>
                            <p className="text-base font-semibold text-gray-900">{viewingForm.ailmentDetails?.isMedicoLegal === 'yes' ? 'Yes' : 'No'}</p>
                          </div>
                          <div className="bg-white p-3 rounded-lg">
                            <span className="text-md font-semibold text-gray-600 block mb-1">Reported to Police</span>
                            <p className="text-base font-semibold text-gray-900">{viewingForm.ailmentDetails?.reportedToPolice === 'yes' ? 'Yes' : 'No'}</p>
                          </div>
                          {viewingForm.ailmentDetails?.reportedToPolice === 'yes' && viewingForm.ailmentDetails?.firNumber && (
                            <div className="bg-white p-3 rounded-lg">
                              <span className="text-md font-semibold text-gray-600 block mb-1">FIR Number</span>
                              <p className="text-base font-semibold text-gray-900">{viewingForm.ailmentDetails.firNumber}</p>
                            </div>
                          )}
                          {viewingForm.ailmentDetails?.reportedToPolice === 'no' && viewingForm.ailmentDetails?.policeNotReportedReason && (
                            <div className="bg-white p-3 rounded-lg md:col-span-2">
                              <span className="text-md font-semibold text-gray-600 block mb-1">Reason for not reporting to police</span>
                              <p className="text-base font-semibold text-gray-900">{viewingForm.ailmentDetails.policeNotReportedReason}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Documents Checklist */}
                <div className="bg-white rounded-lg shadow-sm p-5 border-l-4 border-[#2D869C]">
                  <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                    <CheckSquare className="w-6 h-6 mr-2 text-[#2D869C]" />
                    Documents Submitted
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {Object.entries(viewingForm.documents || {}).map(([key, value]) => {
                      if (key === 'othersSpecify' || typeof value !== 'boolean') return null;
                      return value && (
                        <div key={key} className="flex items-center bg-green-50 p-3 rounded-lg border border-green-200">
                          <CheckSquare className="w-5 h-5 mr-2 text-green-600 flex-shrink-0" />
                          <span className="text-base font-semibold text-gray-700">{key.replace(/([A-Z])/g, ' $1').trim()}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-white border-t-2 border-gray-200 px-6 py-4 flex justify-between items-center rounded-b-lg shadow-lg">
                <div className="text-sm text-gray-600">
                 
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => handlePrintForm(viewingForm)}
                    className="flex items-center px-5 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-base font-semibold"
                    title="Print filled claim form"
                  >
                    <Printer className="w-5 h-5 mr-2" />
                    Print
                  </button>
                  <button
                    onClick={() => handleDownloadSavedFormPdf(viewingForm)}
                    className="flex items-center px-5 py-2.5 bg-[#2D869C] text-white rounded-lg hover:bg-[#9DC9D4] transition-colors text-base font-semibold"
                    title="Download as PDF"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download PDF
                  </button>
                  <button
                    onClick={handleCloseView}
                    className="px-5 py-2.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-base font-semibold"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="bg-white rounded-xl border-2 border-gray-200 p-6 mb-6 print:shadow-none shadow-sm">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <h1 className="text-3xl font-bold text-black">Claim Form</h1>
              
              {/* Form Type Selector */}
              <div className="relative print:hidden">
                <select
                  value={formType}
                  onChange={(e) => setFormType(e.target.value)}
                  className="px-5 py-2.5 bg-white border-2 border-[#2D869C] rounded-xl text-lg font-medium text-gray-700 hover:border-[#2D869C]/80 focus:outline-none focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] appearance-none pr-12 cursor-pointer transition-all shadow-sm"
                >
                  <option value="A">Form A </option>
                  <option value="B">Form B </option>
                </select>
                <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[#2D869C] pointer-events-none" />
              </div>
            </div>
            {activeTab === 'new' && (
              <div className="flex gap-3 print:hidden">
                <button
                  onClick={handlePrint}
                  className="flex items-center px-5 py-2.5 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium text-lg"
                >
                  <Printer className="w-5 h-5 mr-2" />
                  Print
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !selectedPatient}
                  className="flex items-center px-6 py-2.5 bg-[#2D869C] text-white rounded-xl hover:bg-[#2D869C]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium text-lg shadow-sm hover:shadow-md"
                >
                  <Save className="w-5 h-5 mr-2" />
                  {loading ? 'Saving...' : 'Save Form'}
                </button>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="inline-flex gap-3 mt-6 print:hidden bg-gray-100 p-2 rounded-full">
            <button
              onClick={() => setActiveTab('new')}
              className={`flex items-center px-6 py-2.5 font-medium transition-all rounded-full text-lg ${
                activeTab === 'new'
                  ? 'bg-[#2D869C] text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
              }`}
            >
              <FileText className={`w-5 h-5 mr-2`} />
              New Form
            </button>
            <button
              onClick={() => setActiveTab('saved')}
              className={`flex items-center px-6 py-2.5 font-medium transition-all rounded-full text-lg ${
                activeTab === 'saved'
                  ? 'bg-[#2D869C] text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-200'
              }`}
            >
              <List className={`w-5 h-5 mr-2`} />
              Saved Forms
            </button>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'new' ? (
          <form onSubmit={handleSubmit} className="space-y-6">
          
          {/* Form Type Conditional Rendering */}
          {formType === 'B' ? (
            <>
          {/* Patient Search Section */}
          <div className="bg-white rounded-lg border-2 border-gray-200 p-6 print:hidden">
            <h2 className="text-xl font-semibold mb-4 pb-3 border-b-2 border-[#9DC9D4]/30 flex items-center text-gray-800">
              <Search className="w-5 h-5 mr-2 text-[#2D869C]" />
              Search Patient
            </h2>
            
            <div className="relative">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, mobile, or UHID..."
                  value={patientSearch}
                  onChange={(e) => handlePatientSearch(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium placeholder-gray-400 hover:border-[#2D869C] transition-all shadow-sm"
                />
              </div>
              
              {patientSearchResults.length > 0 && (
                <div className="absolute z-10 w-full mt-2 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {patientSearchResults.map((patient) => (
                    <div
                      key={patient._id}
                      onClick={() => selectPatient(patient)}
                      className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                    >
                      <div className="font-semibold text-gray-800">
                        {patient.firstName} {patient.lastName}
                      </div>
                      <div className="text-sm text-gray-500 mt-0.5">
                        Mobile: {patient.mobile}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selectedPatient && (
              <div className="mt-4 p-3 bg-green-50 border-2 border-green-200 rounded-lg">
                <div className="flex items-center text-gray-800">
                  <CheckSquare className="w-5 h-5 mr-2 text-green-600" />
                  <span className="font-medium">Selected: {selectedPatient.firstName} {selectedPatient.lastName}</span>
                </div>
              </div>
            )}
          </div>

          {/* Appointment Selection Section */}
          {selectedPatient && (
            <div className="bg-white rounded-lg border-2 border-gray-200 p-6 print:hidden">
              <h2 className="text-xl font-semibold mb-4 pb-3 border-b-2 border-[#9DC9D4]/30 flex items-center text-gray-800">
                <Calendar className="w-5 h-5 mr-2 text-[#2D869C]" />
                Select Appointment
              </h2>
              
              {loadingAppointments ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#2D869C] mx-auto"></div>
                  <p className="mt-3 text-gray-500">Loading appointments...</p>
                </div>
              ) : patientAppointments.length === 0 ? (
                <div className="p-4 bg-amber-50 border-2 border-amber-200 rounded-lg text-center">
                  <p className="text-gray-800 font-medium">No completed appointments found</p>
                  <p className="text-sm text-gray-600 mt-1">Only completed appointments with prescriptions can be used for claims.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {patientAppointments.map((appointment) => {
                    const appointmentDateTime = new Date(appointment.dateTime);
                    const appointmentDate = appointmentDateTime.toLocaleDateString('en-IN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    });
                    const appointmentTime = appointmentDateTime.toLocaleTimeString('en-IN', {
                      hour: '2-digit',
                      minute: '2-digit'
                    });
                    
                    return (
                      <div
                        key={appointment._id}
                        onClick={() => selectAppointment(appointment)}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          selectedAppointment?._id === appointment._id
                            ? 'border-[#2D869C] bg-[#9DC9D4]/10'
                            : 'border-gray-200 hover:border-[#9DC9D4] hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-4">
                              <div>
                                <p className="font-semibold text-gray-800">
                                  {appointmentDate}
                                </p>
                                <p className="text-sm text-gray-500 mt-0.5">
                                  {appointmentTime}
                                </p>
                              </div>
                              <div className="border-l-2 border-gray-200 pl-4">
                                <p className="text-xs text-gray-500">Doctor</p>
                                <p className="font-medium text-gray-800">
                                  {appointment.doctor?.name || 'N/A'}
                                </p>
                              </div>
                              <div className="border-l-2 border-gray-200 pl-4">
                                <p className="text-xs text-gray-500">Complaint</p>
                                <p className="font-medium text-gray-800">
                                  {appointment.complaint || 'General'}
                                </p>
                              </div>
                            </div>
                          </div>
                          {selectedAppointment?._id === appointment._id && (
                            <CheckSquare className="w-5 h-5 text-[#2D869C]" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {selectedAppointment && (
                <div className="mt-4 p-3 bg-blue-50 border-2 border-blue-200 rounded-lg">
                  <div className="flex items-center text-gray-800">
                    <CheckSquare className="w-5 h-5 mr-2 text-blue-600" />
                    <span className="text-sm font-medium">
                      Appointment selected - Form auto-filled from prescription
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Section A: Hospital Details */}
          <div className="bg-white rounded-xl border-2 border-gray-200 p-6 shadow-sm">
            <h2 className="text-2xl font-semibold mb-5 pb-4 border-b-2 border-[#9DC9D4]/30 flex items-center text-black">
              <Building2 className="w-6 h-6 mr-3 text-[#2D869C]" />
              A. Hospital Details
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Hospital Name *
                </label>
                <input
                  type="text"
                  value={formData.hospitalName}
                  onChange={(e) => handleChange('hospitalName', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Hospital ID *
                </label>
                <input
                  type="text"
                  value={formData.hospitalId}
                  onChange={(e) => handleChange('hospitalId', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Treating Doctor *
                </label>
                <input
                  type="text"
                  value={formData.treatingDoctor}
                  onChange={(e) => handleChange('treatingDoctor', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Qualification *
                </label>
                <input
                  type="text"
                  value={formData.doctorQualification}
                  onChange={(e) => handleChange('doctorQualification', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Hospital Type *
                </label>
                <div className="flex gap-6 mt-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value="network"
                      checked={formData.hospitalType === 'network'}
                      onChange={(e) => handleChange('hospitalType', e.target.value)}
                      className="mr-3 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                    />
                    <span className="text-lg text-gray-700 font-medium">Network</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value="non-network"
                      checked={formData.hospitalType === 'non-network'}
                      onChange={(e) => handleChange('hospitalType', e.target.value)}
                      className="mr-3 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                    />
                    <span className="text-lg text-gray-700 font-medium">Non-Network</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Registration No. *
                </label>
                <input
                  type="text"
                  value={formData.stateRegistrationNo}
                  onChange={(e) => handleChange('stateRegistrationNo', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Phone No. *
                </label>
                <input
                  type="tel"
                  value={formData.hospitalPhone}
                  onChange={(e) => handleChange('hospitalPhone', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  required
                />
              </div>
            </div>
          </div>

          {/* Section B: Patient Details */}
          <div className="bg-white rounded-xl border-2 border-gray-200 p-6 shadow-sm">
            <h2 className="text-2xl font-semibold mb-5 pb-4 border-b-2 border-[#9DC9D4]/30 flex items-center text-black">
              <User className="w-6 h-6 mr-3 text-[#2D869C]" />
              B. Patient Details
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              <div className="lg:col-span-2">
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Patient Name * (Last, First, Middle)
                </label>
                <input
                  type="text"
                  value={formData.patientName}
                  onChange={(e) => handleChange('patientName', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  IP Registration Number *
                </label>
                <input
                  type="text"
                  value={formData.ipRegistrationNumber}
                  onChange={(e) => handleChange('ipRegistrationNumber', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Gender *
                </label>
                <div className="flex gap-6 mt-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value="male"
                      checked={formData.gender === 'male'}
                      onChange={(e) => handleChange('gender', e.target.value)}
                      className="mr-3 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                    />
                    <span className="text-lg text-gray-700 font-medium">Male</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value="female"
                      checked={formData.gender === 'female'}
                      onChange={(e) => handleChange('gender', e.target.value)}
                      className="mr-3 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                    />
                    <span className="text-lg text-gray-700 font-medium">Female</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Age (Years) *
                </label>
                <input
                  type="number"
                  value={formData.age}
                  onChange={(e) => handleChange('age', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Age (Months)
                </label>
                <input
                  type="number"
                  value={formData.ageMonths}
                  onChange={(e) => handleChange('ageMonths', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                />
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Date of Birth *
                </label>
                <input
                  type="date"
                  value={formData.dateOfBirth}
                  onChange={(e) => handleChange('dateOfBirth', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Date of Admission *
                </label>
                <input
                  type="date"
                  value={formData.dateOfAdmission}
                  onChange={(e) => handleChange('dateOfAdmission', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Time of Admission *
                </label>
                <input
                  type="time"
                  value={formData.timeOfAdmission}
                  onChange={(e) => handleChange('timeOfAdmission', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Date of Discharge *
                </label>
                <input
                  type="date"
                  value={formData.dateOfDischarge}
                  onChange={(e) => handleChange('dateOfDischarge', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Time of Discharge *
                </label>
                <input
                  type="time"
                  value={formData.timeOfDischarge}
                  onChange={(e) => handleChange('timeOfDischarge', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  required
                />
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Type of Admission *
                </label>
                <div className="relative">
                  <select
                    value={formData.typeOfAdmission}
                    onChange={(e) => handleChange('typeOfAdmission', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 appearance-none pr-10 cursor-pointer hover:border-[#2D869C] transition-all shadow-sm"
                    required
                  >
                    <option value="emergency">Emergency</option>
                    <option value="planned">Planned</option>
                    <option value="daycare">Day Care</option>
                    <option value="maternity">Maternity</option>
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              </div>

              {formData.typeOfAdmission === 'maternity' && (
                <>
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Date of Delivery
                    </label>
                    <input
                      type="date"
                      value={formData.dateOfDelivery}
                      onChange={(e) => handleChange('dateOfDelivery', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Gravida Status
                    </label>
                    <input
                      type="text"
                      value={formData.gravidaStatus}
                      onChange={(e) => handleChange('gravidaStatus', e.target.value)}
                      placeholder="e.g., G2P1L1"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>
                </>
              )}

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Discharge Status *
                </label>
                <div className="relative">
                  <select
                    value={formData.dischargeStatus}
                    onChange={(e) => handleChange('dischargeStatus', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 appearance-none pr-10 cursor-pointer hover:border-[#2D869C] transition-all shadow-sm"
                    required
                  >
                    <option value="home">Discharge to Home</option>
                    <option value="another_hospital">Discharge to Another Hospital</option>
                    <option value="deceased">Deceased</option>
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Total Claimed Amount (?) *
                </label>
                <input
                  type="text"
                  value={formData.totalClaimedAmount}
                  onChange={(e) => {
                    // Allow only numbers
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    handleChange('totalClaimedAmount', val);
                  }}
                  onBlur={(e) => {
                    // Pad with leading zeros to 7 digits on blur
                    const val = e.target.value.replace(/[^0-9]/g, '');
                    if (val) {
                      handleChange('totalClaimedAmount', val.padStart(7, '0'));
                    }
                  }}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  placeholder="0000000"
                  maxLength={7}
                  required
                />
              </div>
            </div>
          </div>

          {/* Section C: Ailment Details */}
          <div className="bg-white rounded-xl border-2 border-gray-200 p-6 shadow-sm">
            <h2 className="text-2xl font-semibold mb-5 pb-4 border-b-2 border-[#9DC9D4]/30 flex items-center text-black">
              <FileText className="w-6 h-6 mr-3 text-[#2D869C]" />
              C. Ailment Details
            </h2>

            {/* Primary Diagnosis */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 text-lg mb-4">Primary Diagnosis *</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    ICD-10 Code
                  </label>
                  <div className="relative">
                    <select
                      value={formData.primaryDiagnosisCode}
                      onChange={(e) => {
                        const selectedCode = e.target.value;
                        const selectedDisease = availableDiseases.find(d => d.code === selectedCode);
                        handleChange('primaryDiagnosisCode', selectedCode);
                        if (selectedDisease) {
                          handleChange('primaryDiagnosisDescription', selectedDisease.name);
                        }
                      }}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 appearance-none pr-10 cursor-pointer hover:border-[#2D869C] transition-all shadow-sm"
                      required
                    >
                      <option value="">Select ICD-10 Code</option>
                      {availableDiseases.map((disease) => (
                        <option key={disease.code} value={disease.code}>
                          {disease.code} - {disease.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={formData.primaryDiagnosisDescription}
                    onChange={(e) => handleChange('primaryDiagnosisDescription', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Additional Diagnosis */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 text-lg mb-4">Additional Diagnosis</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    ICD-10 Code
                  </label>
                  <div className="relative">
                    <select
                      value={formData.additionalDiagnosisCode}
                      onChange={(e) => {
                        const selectedCode = e.target.value;
                        const selectedDisease = availableDiseases.find(d => d.code === selectedCode);
                        handleChange('additionalDiagnosisCode', selectedCode);
                        if (selectedDisease) {
                          handleChange('additionalDiagnosisDescription', selectedDisease.name);
                        }
                      }}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 appearance-none pr-10 cursor-pointer hover:border-[#2D869C] transition-all shadow-sm"
                    >
                      <option value="">Select ICD-10 Code</option>
                      {availableDiseases.map((disease) => (
                        <option key={disease.code} value={disease.code}>
                          {disease.code} - {disease.name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <input
                    type="text"
                    value={formData.additionalDiagnosisDescription}
                    onChange={(e) => handleChange('additionalDiagnosisDescription', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  />
                </div>
              </div>
            </div>

            {/* Co-morbidities */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 text-lg mb-4">Co-morbidities</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Co-morbidity 1 - ICD-10 Code
                    </label>
                    <div className="relative">
                      <select
                        value={formData.comorbidity1Code}
                        onChange={(e) => {
                          const selectedCode = e.target.value;
                          const selectedDisease = availableDiseases.find(d => d.code === selectedCode);
                          handleChange('comorbidity1Code', selectedCode);
                          if (selectedDisease) {
                            handleChange('comorbidity1Description', selectedDisease.name);
                          }
                        }}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 appearance-none pr-10 cursor-pointer hover:border-[#2D869C] transition-all shadow-sm"
                      >
                        <option value="">Select ICD-10 Code</option>
                        {availableDiseases.map((disease) => (
                          <option key={disease.code} value={disease.code}>
                            {disease.code} - {disease.name}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={formData.comorbidity1Description}
                      onChange={(e) => handleChange('comorbidity1Description', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Co-morbidity 2 - ICD-10 Code
                    </label>
                    <div className="relative">
                      <select
                        value={formData.comorbidity2Code}
                        onChange={(e) => {
                          const selectedCode = e.target.value;
                          const selectedDisease = availableDiseases.find(d => d.code === selectedCode);
                          handleChange('comorbidity2Code', selectedCode);
                          if (selectedDisease) {
                            handleChange('comorbidity2Description', selectedDisease.name);
                          }
                        }}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 appearance-none pr-10 cursor-pointer hover:border-[#2D869C] transition-all shadow-sm"
                      >
                        <option value="">Select ICD-10 Code</option>
                        {availableDiseases.map((disease) => (
                          <option key={disease.code} value={disease.code}>
                            {disease.code} - {disease.name}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={formData.comorbidity2Description}
                      onChange={(e) => handleChange('comorbidity2Description', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Procedures */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 text-lg mb-4">Procedures</h3>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Procedure 1 - ICD-10 PCS Code
                    </label>
                    <input
                      type="text"
                      value={formData.procedure1Code}
                      onChange={(e) => handleChange('procedure1Code', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={formData.procedure1Description}
                      onChange={(e) => handleChange('procedure1Description', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Procedure 2 - ICD-10 PCS Code
                    </label>
                    <input
                      type="text"
                      value={formData.procedure2Code}
                      onChange={(e) => handleChange('procedure2Code', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={formData.procedure2Description}
                      onChange={(e) => handleChange('procedure2Description', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Procedure 3 - ICD-10 PCS Code
                    </label>
                    <input
                      type="text"
                      value={formData.procedure3Code}
                      onChange={(e) => handleChange('procedure3Code', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <input
                      type="text"
                      value={formData.procedure3Description}
                      onChange={(e) => handleChange('procedure3Description', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    Details of Procedure
                  </label>
                  <textarea
                    value={formData.procedureDetails}
                    onChange={(e) => handleChange('procedureDetails', e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  />
                </div>
              </div>
            </div>

            {/* Pre-authorization */}
            <div className="mb-6">
              <h3 className="font-semibold text-gray-800 text-lg mb-4">Pre-authorization</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    Pre-authorization Obtained? *
                  </label>
                  <div className="flex gap-6 mt-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="yes"
                        checked={formData.preAuthObtained === 'yes'}
                        onChange={(e) => handleChange('preAuthObtained', e.target.value)}
                        className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                      />
                      <span className="text-lg text-gray-700">Yes</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="no"
                        checked={formData.preAuthObtained === 'no'}
                        onChange={(e) => handleChange('preAuthObtained', e.target.value)}
                        className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                      />
                      <span className="text-lg text-gray-700">No</span>
                    </label>
                  </div>
                </div>

                {formData.preAuthObtained === 'yes' && (
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Pre-authorization Number
                    </label>
                    <input
                      type="text"
                      value={formData.preAuthNumber}
                      onChange={(e) => handleChange('preAuthNumber', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>
                )}

                {formData.preAuthObtained === 'no' && (
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Reason for Not Obtaining Authorization
                    </label>
                    <input
                      type="text"
                      value={formData.authNotObtainedReason}
                      onChange={(e) => handleChange('authNotObtainedReason', e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Injury Details */}
            <div className="mb-6 border-t border-gray-200 pt-6">
              <h3 className="font-semibold text-gray-800 text-lg mb-4">Hospitalization Due to Injury</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    Is hospitalization due to injury? *
                  </label>
                  <div className="flex gap-6 mt-2">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="yes"
                        checked={formData.hospitalizationDueToInjury === 'yes'}
                        onChange={(e) => handleChange('hospitalizationDueToInjury', e.target.value)}
                        className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                      />
                      <span className="text-lg text-gray-700">Yes</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        value="no"
                        checked={formData.hospitalizationDueToInjury === 'no'}
                        onChange={(e) => handleChange('hospitalizationDueToInjury', e.target.value)}
                        className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                      />
                      <span className="text-lg text-gray-700">No</span>
                    </label>
                  </div>
                </div>

                {formData.hospitalizationDueToInjury === 'yes' && (
                  <>
                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Cause of Injury
                      </label>
                      <div className="relative">
                        <select
                          value={formData.injuryCause}
                          onChange={(e) => handleChange('injuryCause', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 appearance-none pr-10 cursor-pointer hover:border-[#2D869C] transition-all shadow-sm"
                        >
                          <option value="">Select Cause</option>
                          <option value="self_inflicted">Self-inflicted</option>
                          <option value="road_accident">Road Traffic Accident</option>
                          <option value="substance_abuse">Substance Abuse / Alcohol Consumption</option>
                          <option value="others">Others</option>
                        </select>
                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                      </div>
                    </div>

                    {formData.injuryCause === 'others' && (
                      <div>
                        <label className="block text-lg font-medium text-gray-700 mb-2">
                          Specify Other Cause
                        </label>
                        <input
                          type="text"
                          value={formData.injuryOtherCause}
                          onChange={(e) => handleChange('injuryOtherCause', e.target.value)}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                        />
                      </div>
                    )}

                    {formData.injuryCause === 'substance_abuse' && (
                      <div>
                        <label className="block text-lg font-medium text-gray-700 mb-2">
                          Test Conducted to Establish Substance Abuse?
                        </label>
                        <div className="flex gap-6 mt-2">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              value="yes"
                              checked={formData.substanceAbuseTestConducted === 'yes'}
                              onChange={(e) => handleChange('substanceAbuseTestConducted', e.target.value)}
                              className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                            />
                            <span className="text-lg text-gray-700">Yes (Attach Reports)</span>
                          </label>
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              value="no"
                              checked={formData.substanceAbuseTestConducted === 'no'}
                              onChange={(e) => handleChange('substanceAbuseTestConducted', e.target.value)}
                              className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                            />
                            <span className="text-lg text-gray-700">No</span>
                          </label>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Is this a Medico Legal Case?
                      </label>
                      <div className="flex gap-6 mt-2">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            value="yes"
                            checked={formData.isMedicoLegal === 'yes'}
                            onChange={(e) => handleChange('isMedicoLegal', e.target.value)}
                            className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                          />
                          <span className="text-lg text-gray-700">Yes</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            value="no"
                            checked={formData.isMedicoLegal === 'no'}
                            onChange={(e) => handleChange('isMedicoLegal', e.target.value)}
                            className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                          />
                          <span className="text-lg text-gray-700">No</span>
                        </label>
                      </div>
                    </div>

                    {formData.isMedicoLegal === 'yes' && (
                      <>
                        <div>
                          <label className="block text-lg font-medium text-gray-700 mb-2">
                            Reported to Police?
                          </label>
                          <div className="flex gap-6 mt-2">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="radio"
                                value="yes"
                                checked={formData.reportedToPolice === 'yes'}
                                onChange={(e) => handleChange('reportedToPolice', e.target.value)}
                                className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                              />
                              <span className="text-lg text-gray-700">Yes</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="radio"
                                value="no"
                                checked={formData.reportedToPolice === 'no'}
                                onChange={(e) => handleChange('reportedToPolice', e.target.value)}
                                className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                              />
                              <span className="text-lg text-gray-700">No</span>
                            </label>
                          </div>
                        </div>

                        {formData.reportedToPolice === 'yes' && (
                          <div>
                            <label className="block text-lg font-medium text-gray-700 mb-2">
                              FIR Number
                            </label>
                            <input
                              type="text"
                              value={formData.firNumber}
                              onChange={(e) => handleChange('firNumber', e.target.value)}
                              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                            />
                          </div>
                        )}

                        {formData.reportedToPolice === 'no' && (
                          <div>
                            <label className="block text-lg font-medium text-gray-700 mb-2">
                              Reason for Not Reporting to Police
                            </label>
                            <input
                              type="text"
                              value={formData.policeNotReportedReason}
                              onChange={(e) => handleChange('policeNotReportedReason', e.target.value)}
                              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                            />
                          </div>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Section D: Claim Documents Checklist */}
          <div className="bg-white rounded-xl border-2 border-gray-200 p-6 shadow-sm">
            <h2 className="text-2xl font-semibold mb-5 pb-4 border-b-2 border-[#9DC9D4]/30 flex items-center text-gray-800">
              <CheckSquare className="w-6 h-6 mr-2 text-[#2D869C]" />
              D. Document Checklist
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-all hover:border-[#2D869C]">
                <input
                  type="checkbox"
                  checked={formData.documents.claimFormSigned}
                  onChange={(e) => handleDocumentChange('claimFormSigned', e.target.checked)}
                  className="h-5 w-5 text-[#2D869C] focus:ring-[#2D869C] border-gray-300 rounded"
                />
                <span className="ml-3 text-lg text-gray-700">Claim Form duly signed</span>
              </label>

              <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-all hover:border-[#2D869C]">
                <input
                  type="checkbox"
                  checked={formData.documents.preAuthRequest}
                  onChange={(e) => handleDocumentChange('preAuthRequest', e.target.checked)}
                  className="h-5 w-5 text-[#2D869C] focus:ring-[#2D869C] border-gray-300 rounded"
                />
                <span className="ml-3 text-lg text-gray-700">Original Pre-authorization request</span>
              </label>

              <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-all hover:border-[#2D869C]">
                <input
                  type="checkbox"
                  checked={formData.documents.preAuthApproval}
                  onChange={(e) => handleDocumentChange('preAuthApproval', e.target.checked)}
                  className="h-5 w-5 text-[#2D869C] focus:ring-[#2D869C] border-gray-300 rounded"
                />
                <span className="ml-3 text-lg text-gray-700">Copy of Pre-authorization approval letter</span>
              </label>

              <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-all hover:border-[#2D869C]">
                <input
                  type="checkbox"
                  checked={formData.documents.photoIdVerified}
                  onChange={(e) => handleDocumentChange('photoIdVerified', e.target.checked)}
                  className="h-5 w-5 text-[#2D869C] focus:ring-[#2D869C] border-gray-300 rounded"
                />
                <span className="ml-3 text-lg text-gray-700">Copy of Photo ID Card of patient (Verified by hospital)</span>
              </label>

              <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-all hover:border-[#2D869C]">
                <input
                  type="checkbox"
                  checked={formData.documents.dischargeSummary}
                  onChange={(e) => handleDocumentChange('dischargeSummary', e.target.checked)}
                  className="h-5 w-5 text-[#2D869C] focus:ring-[#2D869C] border-gray-300 rounded"
                />
                <span className="ml-3 text-lg text-gray-700">Hospital Discharge summary</span>
              </label>

              <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-all hover:border-[#2D869C]">
                <input
                  type="checkbox"
                  checked={formData.documents.otNotes}
                  onChange={(e) => handleDocumentChange('otNotes', e.target.checked)}
                  className="h-5 w-5 text-[#2D869C] focus:ring-[#2D869C] border-gray-300 rounded"
                />
                <span className="ml-3 text-lg text-gray-700">Operation Theatre Notes</span>
              </label>

              <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-all hover:border-[#2D869C]">
                <input
                  type="checkbox"
                  checked={formData.documents.hospitalMainBill}
                  onChange={(e) => handleDocumentChange('hospitalMainBill', e.target.checked)}
                  className="h-5 w-5 text-[#2D869C] focus:ring-[#2D869C] border-gray-300 rounded"
                />
                <span className="ml-3 text-lg text-gray-700">Hospital main bill</span>
              </label>

              <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-all hover:border-[#2D869C]">
                <input
                  type="checkbox"
                  checked={formData.documents.hospitalBreakupBill}
                  onChange={(e) => handleDocumentChange('hospitalBreakupBill', e.target.checked)}
                  className="h-5 w-5 text-[#2D869C] focus:ring-[#2D869C] border-gray-300 rounded"
                />
                <span className="ml-3 text-lg text-gray-700">Hospital break-up bill</span>
              </label>

              <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-all hover:border-[#2D869C]">
                <input
                  type="checkbox"
                  checked={formData.documents.investigationReports}
                  onChange={(e) => handleDocumentChange('investigationReports', e.target.checked)}
                  className="h-5 w-5 text-[#2D869C] focus:ring-[#2D869C] border-gray-300 rounded"
                />
                <span className="ml-3 text-lg text-gray-700">Investigation reports</span>
              </label>

              <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-all hover:border-[#2D869C]">
                <input
                  type="checkbox"
                  checked={formData.documents.ctMrUsgHpeReports}
                  onChange={(e) => handleDocumentChange('ctMrUsgHpeReports', e.target.checked)}
                  className="h-5 w-5 text-[#2D869C] focus:ring-[#2D869C] border-gray-300 rounded"
                />
                <span className="ml-3 text-lg text-gray-700">CT/MR/USG/HPE investigation reports</span>
              </label>

              <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-all hover:border-[#2D869C]">
                <input
                  type="checkbox"
                  checked={formData.documents.doctorReferenceSlip}
                  onChange={(e) => handleDocumentChange('doctorReferenceSlip', e.target.checked)}
                  className="h-5 w-5 text-[#2D869C] focus:ring-[#2D869C] border-gray-300 rounded"
                />
                <span className="ml-3 text-lg text-gray-700">Doctor's reference slip for investigation</span>
              </label>

              <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-all hover:border-[#2D869C]">
                <input
                  type="checkbox"
                  checked={formData.documents.ecg}
                  onChange={(e) => handleDocumentChange('ecg', e.target.checked)}
                  className="h-5 w-5 text-[#2D869C] focus:ring-[#2D869C] border-gray-300 rounded"
                />
                <span className="ml-3 text-lg text-gray-700">ECG</span>
              </label>

              <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-all hover:border-[#2D869C]">
                <input
                  type="checkbox"
                  checked={formData.documents.pharmacyBills}
                  onChange={(e) => handleDocumentChange('pharmacyBills', e.target.checked)}
                  className="h-5 w-5 text-[#2D869C] focus:ring-[#2D869C] border-gray-300 rounded"
                />
                <span className="ml-3 text-lg text-gray-700">Pharmacy bills</span>
              </label>

              <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-all hover:border-[#2D869C]">
                <input
                  type="checkbox"
                  checked={formData.documents.mlcReportsAndFir}
                  onChange={(e) => handleDocumentChange('mlcReportsAndFir', e.target.checked)}
                  className="h-5 w-5 text-[#2D869C] focus:ring-[#2D869C] border-gray-300 rounded"
                />
                <span className="ml-3 text-lg text-gray-700">MLC reports & Police FIR</span>
              </label>

              <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-all hover:border-[#2D869C]">
                <input
                  type="checkbox"
                  checked={formData.documents.deathSummary}
                  onChange={(e) => handleDocumentChange('deathSummary', e.target.checked)}
                  className="h-5 w-5 text-[#2D869C] focus:ring-[#2D869C] border-gray-300 rounded"
                />
                <span className="ml-3 text-lg text-gray-700">Original death summary (if applicable)</span>
              </label>

              <label className="flex items-center p-3 border-2 border-gray-200 rounded-xl hover:bg-gray-50 cursor-pointer transition-all hover:border-[#2D869C]">
                <input
                  type="checkbox"
                  checked={formData.documents.others}
                  onChange={(e) => handleDocumentChange('others', e.target.checked)}
                  className="h-5 w-5 text-[#2D869C] focus:ring-[#2D869C] border-gray-300 rounded"
                />
                <span className="ml-3 text-lg text-gray-700">Any other</span>
              </label>
            </div>

            {formData.documents.others && (
              <div className="mt-4">
                <label className="block text-lg font-medium text-gray-700 mb-2">
                  Please Specify Other Documents
                </label>
                <input
                  type="text"
                  value={formData.documents.othersSpecify}
                  onChange={(e) => handleDocumentChange('othersSpecify', e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                />
              </div>
            )}
          </div>

          {/* Section E: Non-Network Hospital Details */}
          <div className="bg-white rounded-xl border-2 border-gray-200 p-6 shadow-sm">
            <h2 className="text-2xl font-semibold mb-5 pb-4 border-b-2 border-[#9DC9D4]/30 flex items-center text-gray-800">
              <AlertCircle className="w-6 h-6 mr-2 text-[#2D869C]" />
              E. Non-Network Hospital Details
            </h2>
           
            
            {formData.hospitalType === 'non-network' ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2">
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    a) Address of the Hospital *
                  </label>
                  <textarea
                    value={formData.nonNetworkHospitalAddress}
                    onChange={(e) => handleChange('nonNetworkHospitalAddress', e.target.value)}
                    rows={2}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    required={formData.hospitalType === 'non-network'}
                  />
                </div>

                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    value={formData.nonNetworkHospitalCity}
                    onChange={(e) => handleChange('nonNetworkHospitalCity', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    required={formData.hospitalType === 'non-network'}
                  />
                </div>

                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    State *
                  </label>
                  <input
                    type="text"
                    value={formData.nonNetworkHospitalState}
                    onChange={(e) => handleChange('nonNetworkHospitalState', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    required={formData.hospitalType === 'non-network'}
                  />
                </div>

                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    Pin Code *
                  </label>
                  <input
                    type="text"
                    value={formData.nonNetworkHospitalPinCode}
                    onChange={(e) => handleChange('nonNetworkHospitalPinCode', e.target.value)}
                    maxLength="6"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    required={formData.hospitalType === 'non-network'}
                  />
                </div>

                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    b) Phone No. *
                  </label>
                  <input
                    type="tel"
                    value={formData.nonNetworkHospitalPhone}
                    onChange={(e) => handleChange('nonNetworkHospitalPhone', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    required={formData.hospitalType === 'non-network'}
                  />
                </div>

                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    c) Registration No. with State Code *
                  </label>
                  <input
                    type="text"
                    value={formData.nonNetworkHospitalRegistrationNo}
                    onChange={(e) => handleChange('nonNetworkHospitalRegistrationNo', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    required={formData.hospitalType === 'non-network'}
                  />
                </div>

                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    d) Hospital PAN *
                  </label>
                  <input
                    type="text"
                    value={formData.nonNetworkHospitalPan}
                    onChange={(e) => handleChange('nonNetworkHospitalPan', e.target.value)}
                    maxLength="10"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    required={formData.hospitalType === 'non-network'}
                  />
                </div>

                <div>
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    e) Number of Inpatient Beds *
                  </label>
                  <input
                    type="number"
                    value={formData.nonNetworkInpatientBeds}
                    onChange={(e) => handleChange('nonNetworkInpatientBeds', e.target.value)}
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    required={formData.hospitalType === 'non-network'}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-lg font-medium text-gray-700 mb-3">
                    f) Facilities Available in the Hospital *
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="flex items-center space-x-6">
                      <span className="text-lg text-gray-700">i. OT</span>
                      <div className="flex items-center gap-6">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            checked={formData.nonNetworkHasFacilityOT === true}
                            onChange={() => handleChange('nonNetworkHasFacilityOT', true)}
                            className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                          />
                          <span className="text-lg text-gray-700">Yes</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            checked={formData.nonNetworkHasFacilityOT === false}
                            onChange={() => handleChange('nonNetworkHasFacilityOT', false)}
                            className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                          />
                          <span className="text-lg text-gray-700">No</span>
                        </label>
                      </div>
                    </div>

                    <div className="flex items-center space-x-6">
                      <span className="text-lg text-gray-700">ii. ICU</span>
                      <div className="flex items-center gap-6">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            checked={formData.nonNetworkHasFacilityICU === true}
                            onChange={() => handleChange('nonNetworkHasFacilityICU', true)}
                            className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                          />
                          <span className="text-lg text-gray-700">Yes</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            checked={formData.nonNetworkHasFacilityICU === false}
                            onChange={() => handleChange('nonNetworkHasFacilityICU', false)}
                            className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                          />
                          <span className="text-lg text-gray-700">No</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    iii. Others
                  </label>
                  <input
                    type="text"
                    value={formData.nonNetworkOtherFacilities}
                    onChange={(e) => handleChange('nonNetworkOtherFacilities', e.target.value)}
                    placeholder="Specify other facilities available"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                  />
                </div>
              </div>
            ) : (
              <div className="p-6 bg-gray-50 border-2 border-gray-200 rounded-xl text-center text-gray-600">
                <p className="font-medium text-lg">This section is only applicable for Non-Network Hospitals</p>
                <p className="text-base mt-1">Please select "Non-Network" as Hospital Type in Section A to fill these details</p>
              </div>
            )}
          </div>

          {/* Declaration */}
          <div className="bg-[#9DC9D4]/10 border-2 border-[#9DC9D4] rounded-xl p-6">
            <h3 className="font-semibold text-gray-800 text-lg mb-3">DECLARATION BY THE HOSPITAL</h3>
            <p className="text-base text-gray-700 leading-relaxed">
              We hereby declare that the information furnished in this Claim Form is true & correct to the best of our knowledge and belief. 
              If we have made any false or untrue statement, suppression or concealment of any material fact, our right to claim under this 
              claim shall be forfeited.
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end gap-4 print:hidden">
           
            <button
              type="submit"
              disabled={loading || !selectedPatient}
              className="flex items-center px-6 py-3 bg-[#2D869C] text-white rounded-xl hover:bg-[#2D869C]/90 transition-colors font-semibold shadow-md disabled:opacity-50 text-lg"
            >
              <Save className="w-6 h-6 mr-3 text-white" />
              {loading ? 'Saving...' : 'Save Form B'}
            </button>
          </div>
          </>
          ) : (
            /* Form A - Reimbursement Claim Form */
            <div className="space-y-6">
              {/* Patient Search Section for Form A */}
              <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6 print:hidden">
                <h2 className="text-2xl font-bold mb-6 flex items-center text-gray-800">
                  <Search className="w-6 h-6 mr-3 text-[#2D869C]" />
                  Search Patient
                </h2>
                
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search by name, mobile, or UHID..."
                    value={patientSearch}
                    onChange={(e) => handlePatientSearch(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium hover:border-[#2D869C] transition-all shadow-sm"
                  />
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  
                  {patientSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-lg max-h-60 overflow-y-auto">
                      {patientSearchResults.map((patient) => (
                        <div
                          key={patient._id}
                          onClick={() => selectPatient(patient)}
                          className="p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-semibold text-gray-900 text-lg">
                            {patient.firstName} {patient.lastName}
                          </div>
                          <div className="text-base text-gray-600">
                            Mobile: {patient.mobile}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {selectedPatient && (
                  <div className="mt-4 p-4 bg-green-50 border-2 border-green-200 rounded-xl">
                    <div className="flex items-center text-green-800">
                      <CheckSquare className="w-6 h-6 mr-3 text-[#2D869C]" />
                      <span className="font-semibold text-lg">Selected Patient: {selectedPatient.firstName} {selectedPatient.lastName}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Appointment Selection Section for Form A */}
              {selectedPatient && (
                <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 p-6 print:hidden">
                  <h2 className="text-2xl font-bold mb-6 flex items-center text-gray-800">
                    <Calendar className="w-6 h-6 mr-3 text-[#2D869C]" />
                    Select Appointment for Claim
                  </h2>
                  
                  {loadingAppointments ? (
                    <div className="p-8 text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-[#2D869C] mx-auto"></div>
                      <p className="mt-4 text-gray-600 font-semibold text-lg">Loading appointments...</p>
                    </div>
                  ) : patientAppointments.length === 0 ? (
                    <div className="p-6 bg-yellow-50 border-2 border-yellow-300 rounded-xl text-center">
                      <p className="text-yellow-800 font-semibold text-lg">No completed appointments found for this patient.</p>
                      <p className="text-base text-yellow-600 mt-2">Only completed appointments with prescriptions can be used for claims.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {patientAppointments.map((appointment) => {
                        const appointmentDateTime = new Date(appointment.dateTime);
                        const appointmentDate = appointmentDateTime.toLocaleDateString('en-IN', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        });
                        const appointmentTime = appointmentDateTime.toLocaleTimeString('en-IN', {
                          hour: '2-digit',
                          minute: '2-digit'
                        });
                        
                        return (
                          <div
                            key={appointment._id}
                            onClick={() => selectAppointment(appointment)}
                            className={`p-4 border-2 rounded-xl cursor-pointer transition-all ${
                              selectedAppointment?._id === appointment._id
                                ? 'border-[#2D869C] bg-[#9DC9D4]/10'
                                : 'border-gray-200 hover:border-[#2D869C] hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-4">
                                  <div>
                                    <p className="font-semibold text-gray-900 text-lg">
                                      {appointmentDate}
                                    </p>
                                    <p className="text-base text-gray-600">
                                      Time: {appointmentTime}
                                    </p>
                                  </div>
                                  <div className="border-l pl-4">
                                    <p className="text-base text-gray-600">Doctor</p>
                                    <p className="font-medium text-gray-900 text-lg">
                                      {appointment.doctor?.name || 'N/A'}
                                    </p>
                                  </div>
                                  <div className="border-l pl-4">
                                    <p className="text-base text-gray-600">Chief Complaint</p>
                                    <p className="font-medium text-gray-900 text-lg">
                                      {appointment.complaint || 'General Consultation'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              {selectedAppointment?._id === appointment._id && (
                                <CheckSquare className="w-6 h-6 text-blue-600" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {selectedAppointment && (
                    <div className="mt-4 p-4 bg-[#9DC9D4]/10 border-2 border-[#9DC9D4] rounded-xl">
                      <div className="flex items-center text-[#2D869C]">
                        <CheckSquare className="w-6 h-6 mr-3" />
                        <span className="font-semibold text-lg">
                          Appointment selected - Form details auto-filled from prescription
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Section A: Details of Primary Insured */}
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6 shadow-sm">
                <h2 className="text-2xl font-semibold mb-5 pb-4 border-b-2 border-[#9DC9D4]/30 flex items-center text-gray-800">
                  <User className="w-6 h-6 mr-2 text-[#2D869C]" />
                  Section A - Primary Insured Details
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Policy No. *
                    </label>
                    <input
                      type="text"
                      value={formAData.policyNo}
                      onChange={(e) => setFormAData({...formAData, policyNo: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Sl. No / Certificate No.
                    </label>
                    <input
                      type="text"
                      value={formAData.certificateNo}
                      onChange={(e) => setFormAData({...formAData, certificateNo: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Company / TPA ID (MA ID) No.
                    </label>
                    <input
                      type="text"
                      value={formAData.companyTpaId}
                      onChange={(e) => setFormAData({...formAData, companyTpaId: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Name (Surname, First Name, Middle Name) *
                    </label>
                    <input
                      type="text"
                      value={formAData.insuredName}
                      onChange={(e) => setFormAData({...formAData, insuredName: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      required
                    />
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Address *
                    </label>
                    <textarea
                      value={formAData.insuredAddress}
                      onChange={(e) => setFormAData({...formAData, insuredAddress: e.target.value})}
                      rows="2"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      required
                    ></textarea>
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      City *
                    </label>
                    <input
                      type="text"
                      value={formAData.insuredCity}
                      onChange={(e) => setFormAData({...formAData, insuredCity: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      State *
                    </label>
                    <input
                      type="text"
                      value={formAData.insuredState}
                      onChange={(e) => setFormAData({...formAData, insuredState: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Pin Code *
                    </label>
                    <input
                      type="text"
                      value={formAData.insuredPinCode}
                      onChange={(e) => setFormAData({...formAData, insuredPinCode: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Phone No. *
                    </label>
                    <input
                      type="tel"
                      value={formAData.insuredPhone}
                      onChange={(e) => setFormAData({...formAData, insuredPhone: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Email ID
                    </label>
                    <input
                      type="email"
                      value={formAData.insuredEmail}
                      onChange={(e) => setFormAData({...formAData, insuredEmail: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Section B: Details of Insurance History */}
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                <h2 className="text-2xl font-semibold mb-5 pb-4 border-b-2 border-[#9DC9D4]/30 flex items-center text-gray-800">
                  <FileText className="w-6 h-6 mr-2 text-[#2D869C]" />
                  Section B - Insurance History
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Currently covered by any other Mediclaim / Health Insurance?
                    </label>
                    <div className="flex gap-6 mt-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          value="yes"
                          checked={formAData.coveredByOtherInsurance === 'yes'}
                          onChange={(e) => setFormAData({...formAData, coveredByOtherInsurance: e.target.value})}
                          className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                        />
                        <span className="text-lg text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          value="no"
                          checked={formAData.coveredByOtherInsurance === 'no'}
                          onChange={(e) => setFormAData({...formAData, coveredByOtherInsurance: e.target.value})}
                          className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                        />
                        <span className="text-lg text-gray-700">No</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Date of commencement of first Insurance without break
                    </label>
                    <input
                      type="date"
                      value={formAData.firstInsuranceCommencementDate}
                      onChange={(e) => setFormAData({...formAData, firstInsuranceCommencementDate: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  {formAData.coveredByOtherInsurance === 'yes' && (
                    <>
                      <div>
                        <label className="block text-lg font-medium text-gray-700 mb-2">
                          Company Name
                        </label>
                        <input
                          type="text"
                          value={formAData.otherCompanyName}
                          onChange={(e) => setFormAData({...formAData, otherCompanyName: e.target.value})}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                        />
                      </div>

                      <div>
                        <label className="block text-lg font-medium text-gray-700 mb-2">
                          Policy No.
                        </label>
                        <input
                          type="text"
                          value={formAData.otherPolicyNo}
                          onChange={(e) => setFormAData({...formAData, otherPolicyNo: e.target.value})}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Sum Insured (Rs.)
                    </label>
                    <input
                      type="text"
                      value={formAData.sumInsured}
                      onChange={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '');
                        setFormAData({...formAData, sumInsured: val});
                      }}
                      onBlur={(e) => {
                        const val = e.target.value.replace(/[^0-9]/g, '');
                        if (val) {
                          setFormAData({...formAData, sumInsured: val.padStart(7, '0')});
                        }
                      }}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      placeholder="0000000"
                      maxLength={7}
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Hospitalized in the last four years since inception?
                    </label>
                    <div className="flex gap-6 mt-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          value="yes"
                          checked={formAData.hospitalizedInLastFourYears === 'yes'}
                          onChange={(e) => setFormAData({...formAData, hospitalizedInLastFourYears: e.target.value})}
                          className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                        />
                        <span className="text-lg text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          value="no"
                          checked={formAData.hospitalizedInLastFourYears === 'no'}
                          onChange={(e) => setFormAData({...formAData, hospitalizedInLastFourYears: e.target.value})}
                          className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                        />
                        <span className="text-lg text-gray-700">No</span>
                      </label>
                    </div>
                  </div>

                  {formAData.hospitalizedInLastFourYears === 'yes' && (
                    <>
                      <div>
                        <label className="block text-lg font-medium text-gray-700 mb-2">
                          Date
                        </label>
                        <input
                          type="date"
                          value={formAData.lastHospitalizationDate}
                          onChange={(e) => setFormAData({...formAData, lastHospitalizationDate: e.target.value})}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-lg font-medium text-gray-700 mb-2">
                          Diagnosis
                        </label>
                        <input
                          type="text"
                          value={formAData.lastHospitalizationDiagnosis}
                          onChange={(e) => setFormAData({...formAData, lastHospitalizationDiagnosis: e.target.value})}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                        />
                      </div>
                    </>
                  )}

                  <div className="md:col-span-2">
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Previously covered by any other Mediclaim /Health insurance?
                    </label>
                    <div className="flex gap-6 mt-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          value="yes"
                          checked={formAData.previouslyCovered === 'yes'}
                          onChange={(e) => setFormAData({...formAData, previouslyCovered: e.target.value})}
                          className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                        />
                        <span className="text-lg text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          value="no"
                          checked={formAData.previouslyCovered === 'no'}
                          onChange={(e) => setFormAData({...formAData, previouslyCovered: e.target.value})}
                          className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                        />
                        <span className="text-lg text-gray-700">No</span>
                      </label>
                    </div>
                  </div>

                  {formAData.previouslyCovered === 'yes' && (
                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        value={formAData.previousCompanyName}
                        onChange={(e) => setFormAData({...formAData, previousCompanyName: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Section C: Details of Insured Person Hospitalized */}
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                <h2 className="text-2xl font-semibold mb-5 pb-4 border-b-2 border-[#9DC9D4]/30 flex items-center text-gray-800">
                  <User className="w-6 h-6 mr-2 text-[#2D869C]" />
                  Section C - Insured Person Hospitalized
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div className="md:col-span-3">
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Name (Surname, First Name, Middle Name) *
                    </label>
                    <input
                      type="text"
                      value={formAData.patientName}
                      onChange={(e) => setFormAData({...formAData, patientName: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Gender *
                    </label>
                    <div className="relative">
                      <select
                        value={formAData.patientGender}
                        onChange={(e) => setFormAData({...formAData, patientGender: e.target.value})}
                        className="w-full px-4 py-3 pr-10 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm appearance-none cursor-pointer"
                        required
                      >
                        <option value="">Select Gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Age (Years)
                    </label>
                    <input
                      type="number"
                      value={formAData.patientAge}
                      onChange={(e) => setFormAData({...formAData, patientAge: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Age (Months)
                    </label>
                    <input
                      type="number"
                      value={formAData.patientAgeMonths}
                      onChange={(e) => setFormAData({...formAData, patientAgeMonths: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      value={formAData.patientDob}
                      onChange={(e) => setFormAData({...formAData, patientDob: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Relationship to Primary Insured *
                    </label>
                    <div className="flex flex-wrap gap-6 mt-2">
                      {['self', 'spouse', 'child', 'father', 'mother', 'other'].map((rel) => (
                        <label key={rel} className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            value={rel}
                            checked={formAData.relationshipToPrimary === rel}
                            onChange={(e) => setFormAData({...formAData, relationshipToPrimary: e.target.value})}
                            className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                          />
                          <span className="capitalize text-lg text-gray-700">{rel}</span>
                        </label>
                      ))}
                    </div>
                    {formAData.relationshipToPrimary === 'other' && (
                      <input
                        type="text"
                        placeholder="Please specify"
                        value={formAData.relationshipOther}
                        onChange={(e) => setFormAData({...formAData, relationshipOther: e.target.value})}
                        className="w-full mt-2 px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      />
                    )}
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Occupation
                    </label>
                    <div className="flex flex-wrap gap-6 mt-2">
                      {['service', 'self_employed', 'home_maker', 'student', 'retired', 'other'].map((occ) => (
                        <label key={occ} className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            value={occ}
                            checked={formAData.patientOccupation === occ}
                            onChange={(e) => setFormAData({...formAData, patientOccupation: e.target.value})}
                            className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                          />
                          <span className="capitalize text-lg text-gray-700">{occ.replace('_', ' ')}</span>
                        </label>
                      ))}
                    </div>
                    {formAData.patientOccupation === 'other' && (
                      <input
                        type="text"
                        placeholder="Please specify"
                        value={formAData.patientOccupationOther}
                        onChange={(e) => setFormAData({...formAData, patientOccupationOther: e.target.value})}
                        className="w-full mt-2 px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      />
                    )}
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Address (if different from above)
                    </label>
                    <textarea
                      value={formAData.patientAddress}
                      onChange={(e) => setFormAData({...formAData, patientAddress: e.target.value})}
                      rows="2"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    ></textarea>
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      City
                    </label>
                    <input
                      type="text"
                      value={formAData.patientCity}
                      onChange={(e) => setFormAData({...formAData, patientCity: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      State
                    </label>
                    <input
                      type="text"
                      value={formAData.patientState}
                      onChange={(e) => setFormAData({...formAData, patientState: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Pin Code
                    </label>
                    <input
                      type="text"
                      value={formAData.patientPinCode}
                      onChange={(e) => setFormAData({...formAData, patientPinCode: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Phone No.
                    </label>
                    <input
                      type="tel"
                      value={formAData.patientPhone}
                      onChange={(e) => setFormAData({...formAData, patientPhone: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Email ID
                    </label>
                    <input
                      type="email"
                      value={formAData.patientEmail}
                      onChange={(e) => setFormAData({...formAData, patientEmail: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Section D: Details of Hospitalization */}
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                <h2 className="text-2xl font-semibold mb-5 pb-4 border-b-2 border-[#9DC9D4]/30 flex items-center text-gray-800">
                  <Building2 className="w-6 h-6 mr-2 text-[#2D869C]" />
                  D. Hospitalization Details
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div className="md:col-span-3">
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Name of Hospital where Admitted *
                    </label>
                    <input
                      type="text"
                      value={formAData.hospitalName}
                      onChange={(e) => setFormAData({...formAData, hospitalName: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Room Category Occupied *
                    </label>
                    <div className="relative">
                      <select
                        value={formAData.roomCategory}
                        onChange={(e) => setFormAData({...formAData, roomCategory: e.target.value})}
                        className="w-full px-4 py-3 pr-10 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm appearance-none cursor-pointer"
                        required
                      >
                        <option value="">Select</option>
                        <option value="day_care">Day Care</option>
                        <option value="single_occupancy">Single Occupancy</option>
                        <option value="twin_sharing">Twin Sharing</option>
                        <option value="three_or_more">3 or More Beds per Room</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Hospitalization Due To *
                    </label>
                    <div className="relative">
                      <select
                        value={formAData.hospitalizationDueTo}
                        onChange={(e) => setFormAData({...formAData, hospitalizationDueTo: e.target.value})}
                        className="w-full px-4 py-3 pr-10 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm appearance-none cursor-pointer"
                        required
                      >
                        <option value="">Select</option>
                        <option value="injury">Injury</option>
                        <option value="illness">Illness</option>
                        <option value="maternity">Maternity</option>
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Date of Injury / Disease First Detected / Delivery
                    </label>
                    <input
                      type="date"
                      value={formAData.injuryOrDiseaseDate}
                      onChange={(e) => setFormAData({...formAData, injuryOrDiseaseDate: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Date of Admission *
                    </label>
                    <input
                      type="date"
                      value={formAData.dateOfAdmission}
                      onChange={(e) => setFormAData({...formAData, dateOfAdmission: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Time of Admission
                    </label>
                    <input
                      type="time"
                      value={formAData.timeOfAdmission}
                      onChange={(e) => setFormAData({...formAData, timeOfAdmission: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Date of Discharge
                    </label>
                    <input
                      type="date"
                      value={formAData.dateOfDischarge}
                      onChange={(e) => setFormAData({...formAData, dateOfDischarge: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Time of Discharge
                    </label>
                    <input
                      type="time"
                      value={formAData.timeOfDischarge}
                      onChange={(e) => setFormAData({...formAData, timeOfDischarge: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      If Injury Give Cause
                    </label>
                    <div className="flex gap-6 mt-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="injuryCause"
                          value="self_inflicted"
                          checked={formAData.injuryCause === 'self_inflicted'}
                          onChange={(e) => setFormAData({...formAData, injuryCause: e.target.value})}
                          className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                        />
                        <span className="text-lg text-gray-700">Self Inflicted</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="injuryCause"
                          value="road_traffic_accident"
                          checked={formAData.injuryCause === 'road_traffic_accident'}
                          onChange={(e) => setFormAData({...formAData, injuryCause: e.target.value})}
                          className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                        />
                        <span className="text-lg text-gray-700">Road Traffic Accident</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="injuryCause"
                          value="substance_abuse_alcohol"
                          checked={formAData.injuryCause === 'substance_abuse_alcohol'}
                          onChange={(e) => setFormAData({...formAData, injuryCause: e.target.value})}
                          className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                        />
                        <span className="text-lg text-gray-700">Substance Abuse / Alcohol Consumption</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      If Medico Legal
                    </label>
                    <div className="flex gap-6 mt-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="isMedicoLegal"
                          value="yes"
                          checked={formAData.isMedicoLegal === 'yes'}
                          onChange={(e) => setFormAData({...formAData, isMedicoLegal: e.target.value})}
                          className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                        />
                        <span className="text-lg text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="isMedicoLegal"
                          value="no"
                          checked={formAData.isMedicoLegal === 'no'}
                          onChange={(e) => setFormAData({...formAData, isMedicoLegal: e.target.value})}
                          className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                        />
                        <span className="text-lg text-gray-700">No</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Reported to Police
                    </label>
                    <div className="flex gap-6 mt-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="reportedToPolice"
                          value="yes"
                          checked={formAData.reportedToPolice === 'yes'}
                          onChange={(e) => setFormAData({...formAData, reportedToPolice: e.target.value})}
                          className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                        />
                        <span className="text-lg text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="reportedToPolice"
                          value="no"
                          checked={formAData.reportedToPolice === 'no'}
                          onChange={(e) => setFormAData({...formAData, reportedToPolice: e.target.value})}
                          className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                        />
                        <span className="text-lg text-gray-700">No</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      MLC Report & Police FIR Attached
                    </label>
                    <div className="flex gap-6 mt-2">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="mlcReportAttached"
                          value="yes"
                          checked={formAData.mlcReportAttached === 'yes'}
                          onChange={(e) => setFormAData({...formAData, mlcReportAttached: e.target.value})}
                          className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                        />
                        <span className="text-lg text-gray-700">Yes</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="mlcReportAttached"
                          value="no"
                          checked={formAData.mlcReportAttached === 'no'}
                          onChange={(e) => setFormAData({...formAData, mlcReportAttached: e.target.value})}
                          className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                        />
                        <span className="text-lg text-gray-700">No</span>
                      </label>
                    </div>
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      System of Medicine
                    </label>
                    <input
                      type="text"
                      value={formAData.systemOfMedicine}
                      onChange={(e) => setFormAData({...formAData, systemOfMedicine: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      placeholder="e.g., Allopathy, Ayurveda, Homeopathy"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Diagnosis (Current Hospitalization)
                    </label>
                    <input
                      type="text"
                      value={formAData.diagnosis}
                      onChange={(e) => setFormAData({...formAData, diagnosis: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      placeholder="e.g., Acute Appendicitis, Pneumonia"
                    />
                  </div>

                  <div className="md:col-span-1">
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Diagnosis ICD Code
                    </label>
                    <input
                      type="text"
                      value={formAData.diagnosisIcdCode}
                      onChange={(e) => setFormAData({...formAData, diagnosisIcdCode: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      placeholder="e.g., K35.80"
                    />
                  </div>
                </div>
              </div>

              {/* Section E: Details of Claim */}
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                <h2 className="text-2xl font-semibold mb-5 pb-4 border-b-2 border-[#9DC9D4]/30 flex items-center text-gray-800">
                  <FileText className="w-6 h-6 mr-2 text-[#2D869C]" />
                  E. Claim Details
                </h2>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Pre-hospitalization Expenses (Rs.)
                      </label>
                      <input
                        type="number"
                        value={formAData.preHospitalizationExpenses}
                        onChange={(e) => setFormAData({...formAData, preHospitalizationExpenses: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Hospitalization Expenses (Rs.) *
                      </label>
                      <input
                        type="number"
                        value={formAData.hospitalizationExpenses}
                        onChange={(e) => setFormAData({...formAData, hospitalizationExpenses: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                        placeholder="0.00"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Post-hospitalization Expenses (Rs.)
                      </label>
                      <input
                        type="number"
                        value={formAData.postHospitalizationExpenses}
                        onChange={(e) => setFormAData({...formAData, postHospitalizationExpenses: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Health Check-up Cost (Rs.)
                      </label>
                      <input
                        type="number"
                        value={formAData.healthCheckupCost}
                        onChange={(e) => setFormAData({...formAData, healthCheckupCost: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Ambulance Charges (Rs.)
                      </label>
                      <input
                        type="number"
                        value={formAData.ambulanceCharges}
                        onChange={(e) => setFormAData({...formAData, ambulanceCharges: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Others (Rs.)
                      </label>
                      <input
                        type="number"
                        value={formAData.otherExpenses}
                        onChange={(e) => setFormAData({...formAData, otherExpenses: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                        placeholder="0.00"
                      />
                    </div>

                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Pre-hospitalization Period (days)
                      </label>
                      <input
                        type="number"
                        value={formAData.preHospitalizationPeriod}
                        onChange={(e) => setFormAData({...formAData, preHospitalizationPeriod: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Post-hospitalization Period (days)
                      </label>
                      <input
                        type="number"
                        value={formAData.postHospitalizationPeriod}
                        onChange={(e) => setFormAData({...formAData, postHospitalizationPeriod: e.target.value})}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Total Claimed Amount (Rs.) *
                      </label>
                      <input
                        type="text"
                        value={formAData.totalClaimAmount}
                        onChange={(e) => {
                          // Allow only numbers
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          setFormAData({...formAData, totalClaimAmount: val});
                        }}
                        onBlur={(e) => {
                          // Pad with leading zeros to 7 digits on blur
                          const val = e.target.value.replace(/[^0-9]/g, '');
                          if (val) {
                            setFormAData({...formAData, totalClaimAmount: val.padStart(7, '0')});
                          }
                        }}
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-semibold text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                        placeholder="0000000"
                        maxLength={7}
                        required
                      />
                    </div>
                  </div>

                  {/* Domiciliary Hospitalization */}
                  <div className="border-t border-gray-200 pt-4">
                    <div className="mb-4">
                      <label className="block text-lg font-medium text-gray-700 mb-2">
                        Claim for Domiciliary Hospitalization
                      </label>
                      <div className="flex gap-6 mt-2">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="claimForDomiciliary"
                            value="yes"
                            checked={formAData.claimForDomiciliary === 'yes'}
                            onChange={(e) => setFormAData({...formAData, claimForDomiciliary: e.target.value})}
                            className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                          />
                          <span className="text-lg text-gray-700">Yes</span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name="claimForDomiciliary"
                            value="no"
                            checked={formAData.claimForDomiciliary === 'no'}
                            onChange={(e) => setFormAData({...formAData, claimForDomiciliary: e.target.value})}
                            className="mr-2 w-5 h-5 text-[#2D869C] focus:ring-[#2D869C]"
                          />
                          <span className="text-lg text-gray-700">No</span>
                        </label>
                      </div>
                    </div>

                    {formAData.claimForDomiciliary === 'yes' && (
                      <div>
                        <label className="block text-lg font-medium text-gray-700 mb-2">
                          Provide Details in Annexure
                        </label>
                        <textarea
                          value={formAData.domiciliaryDetails}
                          onChange={(e) => setFormAData({...formAData, domiciliaryDetails: e.target.value})}
                          rows="2"
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                        ></textarea>
                      </div>
                    )}
                  </div>

                  {/* Lump Sum / Cash Benefit Claimed */}
                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Details of Lump Sum / Cash Benefit Claimed</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-lg font-medium text-gray-700 mb-2">
                          Hospital Daily Cash (Rs.)
                        </label>
                        <input
                          type="number"
                          value={formAData.hospitalDailyCash}
                          onChange={(e) => setFormAData({...formAData, hospitalDailyCash: e.target.value})}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <label className="block text-lg font-medium text-gray-700 mb-2">
                          Surgical Cash (Rs.)
                        </label>
                        <input
                          type="number"
                          value={formAData.surgicalCash}
                          onChange={(e) => setFormAData({...formAData, surgicalCash: e.target.value})}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <label className="block text-lg font-medium text-gray-700 mb-2">
                          Critical Illness Benefit (Rs.)
                        </label>
                        <input
                          type="number"
                          value={formAData.criticalIllnessBenefit}
                          onChange={(e) => setFormAData({...formAData, criticalIllnessBenefit: e.target.value})}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <label className="block text-lg font-medium text-gray-700 mb-2">
                          Convalescence (Rs.)
                        </label>
                        <input
                          type="number"
                          value={formAData.convalescence}
                          onChange={(e) => setFormAData({...formAData, convalescence: e.target.value})}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <label className="block text-lg font-medium text-gray-700 mb-2">
                          Pre/Post Hospitalization Lump Sum (Rs.)
                        </label>
                        <input
                          type="number"
                          value={formAData.prePostHospitalizationLumpSum}
                          onChange={(e) => setFormAData({...formAData, prePostHospitalizationLumpSum: e.target.value})}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                          placeholder="0.00"
                        />
                      </div>

                      <div>
                        <label className="block text-lg font-medium text-gray-700 mb-2">
                          Others (Rs.)
                        </label>
                        <input
                          type="number"
                          value={formAData.othersLumpSum}
                          onChange={(e) => setFormAData({...formAData, othersLumpSum: e.target.value})}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                          placeholder="0.00"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-lg font-medium text-gray-700 mb-2">
                          Total Lump Sum (Rs.)
                        </label>
                        <input
                          type="number"
                          value={formAData.totalLumpSum}
                          onChange={(e) => setFormAData({...formAData, totalLumpSum: e.target.value})}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-semibold text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                          placeholder="0.00"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Claim Documents Checklist */}
                  <div className="border-t border-gray-200 pt-4">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Claim Documents Submitted - Check List</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formAData.documents.claimFormSigned}
                          onChange={(e) => setFormAData({
                            ...formAData,
                            documents: { ...formAData.documents, claimFormSigned: e.target.checked }
                          })}
                          className="mr-2 w-4 h-4 text-[#2D869C] focus:ring-[#2D869C] rounded"
                        />
                        <span className="text-lg text-gray-700">Claim Form Duly Signed</span>
                      </label>
                      
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formAData.documents.claimIntimationCopy}
                          onChange={(e) => setFormAData({
                            ...formAData,
                            documents: { ...formAData.documents, claimIntimationCopy: e.target.checked }
                          })}
                          className="mr-2 w-4 h-4 text-[#2D869C] focus:ring-[#2D869C] rounded"
                        />
                        <span className="text-lg text-gray-700">Copy of the Claim Intimation, if any</span>
                      </label>

                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formAData.documents.hospitalMainBill}
                          onChange={(e) => setFormAData({
                            ...formAData,
                            documents: { ...formAData.documents, hospitalMainBill: e.target.checked }
                          })}
                          className="mr-2 w-4 h-4 text-[#2D869C] focus:ring-[#2D869C] rounded"
                        />
                        <span className="text-lg text-gray-700">Hospital Main Bill</span>
                      </label>

                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formAData.documents.hospitalBreakupBill}
                          onChange={(e) => setFormAData({
                            ...formAData,
                            documents: { ...formAData.documents, hospitalBreakupBill: e.target.checked }
                          })}
                          className="mr-2 w-4 h-4 text-[#2D869C] focus:ring-[#2D869C] rounded"
                        />
                        <span className="text-lg text-gray-700">Hospital Break-up Bill</span>
                      </label>

                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formAData.documents.hospitalBillPaymentReceipt}
                          onChange={(e) => setFormAData({
                            ...formAData,
                            documents: { ...formAData.documents, hospitalBillPaymentReceipt: e.target.checked }
                          })}
                          className="mr-2 w-4 h-4 text-[#2D869C] focus:ring-[#2D869C] rounded"
                        />
                        <span className="text-lg text-gray-700">Hospital Bill Payment Receipt</span>
                      </label>

                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formAData.documents.hospitalDischargeSummary}
                          onChange={(e) => setFormAData({
                            ...formAData,
                            documents: { ...formAData.documents, hospitalDischargeSummary: e.target.checked }
                          })}
                          className="mr-2 w-4 h-4 text-[#2D869C] focus:ring-[#2D869C] rounded"
                        />
                        <span className="text-lg text-gray-700">Hospital Discharge Summary</span>
                      </label>

                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formAData.documents.pharmacyBill}
                          onChange={(e) => setFormAData({
                            ...formAData,
                            documents: { ...formAData.documents, pharmacyBill: e.target.checked }
                          })}
                          className="mr-2 w-4 h-4 text-[#2D869C] focus:ring-[#2D869C] rounded"
                        />
                        <span className="text-lg text-gray-700">Pharmacy Bill</span>
                      </label>

                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formAData.documents.operationTheaterNotes}
                          onChange={(e) => setFormAData({
                            ...formAData,
                            documents: { ...formAData.documents, operationTheaterNotes: e.target.checked }
                          })}
                          className="mr-2 w-4 h-4 text-[#2D869C] focus:ring-[#2D869C] rounded"
                        />
                        <span className="text-lg text-gray-700">Operation/Theater Notes</span>
                      </label>

                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formAData.documents.ecg}
                          onChange={(e) => setFormAData({
                            ...formAData,
                            documents: { ...formAData.documents, ecg: e.target.checked }
                          })}
                          className="mr-2 w-4 h-4 text-[#2D869C] focus:ring-[#2D869C] rounded"
                        />
                        <span className="text-lg text-gray-700">ECG</span>
                      </label>

                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formAData.documents.doctorsRequestForInvestigation}
                          onChange={(e) => setFormAData({
                            ...formAData,
                            documents: { ...formAData.documents, doctorsRequestForInvestigation: e.target.checked }
                          })}
                          className="mr-2 w-4 h-4 text-[#2D869C] focus:ring-[#2D869C] rounded"
                        />
                        <span className="text-lg text-gray-700">Doctor's Request for Investigation</span>
                      </label>

                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formAData.documents.investigationReports}
                          onChange={(e) => setFormAData({
                            ...formAData,
                            documents: { ...formAData.documents, investigationReports: e.target.checked }
                          })}
                          className="mr-2 w-4 h-4 text-[#2D869C] focus:ring-[#2D869C] rounded"
                        />
                        <span className="text-lg text-gray-700">Investigation Reports (Including CT/MRI/USG/HPE)</span>
                      </label>

                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formAData.documents.doctorsPrescriptions}
                          onChange={(e) => setFormAData({
                            ...formAData,
                            documents: { ...formAData.documents, doctorsPrescriptions: e.target.checked }
                          })}
                          className="mr-2 w-4 h-4 text-[#2D869C] focus:ring-[#2D869C] rounded"
                        />
                        <span className="text-lg text-gray-700">Doctor's Prescriptions</span>
                      </label>

                      <label className="flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formAData.documents.others}
                          onChange={(e) => setFormAData({
                            ...formAData,
                            documents: { ...formAData.documents, others: e.target.checked }
                          })}
                          className="mr-2 w-4 h-4 text-[#2D869C] focus:ring-[#2D869C] rounded"
                        />
                        <span className="text-lg text-gray-700">Others</span>
                      </label>
                    </div>

                    {formAData.documents.others && (
                      <div className="mt-3">
                        <label className="block text-lg font-medium text-gray-700 mb-2">
                          Please Specify Others
                        </label>
                        <input
                          type="text"
                          value={formAData.documents.othersSpecify}
                          onChange={(e) => setFormAData({
                            ...formAData,
                            documents: { ...formAData.documents, othersSpecify: e.target.value }
                          })}
                          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Section F: Details of Bills Enclosed */}
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                <h2 className="text-2xl font-semibold mb-5 pb-4 border-b-2 border-[#9DC9D4]/30 flex items-center text-gray-800">
                  <FileText className="w-6 h-6 mr-2 text-[#2D869C]" />
                  F. Details of Bills Enclosed
                </h2>
                
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Sl. No.</th>
                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Bill No.</th>
                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Date</th>
                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Issued by</th>
                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Towards</th>
                        <th className="border border-gray-300 px-4 py-2 text-left text-sm font-semibold">Amount (Rs)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...Array(10)].map((_, index) => (
                        <tr key={index}>
                          <td className="border border-gray-300 px-4 py-2 text-center">{index + 1}</td>
                          <td className="border border-gray-300 px-2 py-1">
                            <input
                              type="text"
                              value={formAData.bills[index]?.billNo || ''}
                              onChange={(e) => {
                                const newBills = [...formAData.bills];
                                newBills[index] = { ...newBills[index], billNo: e.target.value };
                                setFormAData({...formAData, bills: newBills});
                              }}
                              className="w-full px-2 py-1 border-0 focus:ring-1 focus:ring-blue-500"
                            />
                          </td>
                          <td className="border border-gray-300 px-2 py-1">
                            <input
                              type="date"
                              value={formAData.bills[index]?.date || ''}
                              onChange={(e) => {
                                const newBills = [...formAData.bills];
                                newBills[index] = { ...newBills[index], date: e.target.value };
                                setFormAData({...formAData, bills: newBills});
                              }}
                              className="w-full px-2 py-1 border-0 focus:ring-1 focus:ring-blue-500"
                            />
                          </td>
                          <td className="border border-gray-300 px-2 py-1">
                            <input
                              type="text"
                              value={formAData.bills[index]?.issuedBy || ''}
                              onChange={(e) => {
                                const newBills = [...formAData.bills];
                                newBills[index] = { ...newBills[index], issuedBy: e.target.value };
                                setFormAData({...formAData, bills: newBills});
                              }}
                              className="w-full px-2 py-1 border-0 focus:ring-1 focus:ring-blue-500"
                            />
                          </td>
                          <td className="border border-gray-300 px-2 py-1">
                            <input
                              type="text"
                              value={formAData.bills[index]?.towards || ''}
                              onChange={(e) => {
                                const newBills = [...formAData.bills];
                                newBills[index] = { ...newBills[index], towards: e.target.value };
                                setFormAData({...formAData, bills: newBills});
                              }}
                              className="w-full px-2 py-1 border-0 focus:ring-1 focus:ring-blue-500"
                              placeholder={index === 0 ? "Hospital main Bill" : index === 1 ? "Pre-hospitalization Bills" : index === 2 ? "Post-hospitalization Bills" : index === 3 ? "Pharmacy Bills" : ""}
                            />
                          </td>
                          <td className="border border-gray-300 px-2 py-1">
                            <input
                              type="number"
                              value={formAData.bills[index]?.amount || ''}
                              onChange={(e) => {
                                const newBills = [...formAData.bills];
                                newBills[index] = { ...newBills[index], amount: e.target.value };
                                setFormAData({...formAData, bills: newBills});
                              }}
                              className="w-full px-2 py-1 border-0 focus:ring-1 focus:ring-blue-500"
                              placeholder="0.00"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Section G: Details of Primary Insured's Bank Account */}
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                <h2 className="text-2xl font-semibold mb-5 pb-4 border-b-2 border-[#9DC9D4]/30 flex items-center text-gray-800">
                  <CreditCard className="w-6 h-6 mr-2 text-[#2D869C]" />
                  G. Details of Primary Insured's Bank Account (Optional)
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      PAN
                    </label>
                    <input
                      type="text"
                      value={formAData.pan}
                      onChange={(e) => setFormAData({...formAData, pan: e.target.value.toUpperCase()})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm uppercase"
                      maxLength="10"
                      placeholder="ABCDE1234F"
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Account Number
                    </label>
                    <input
                      type="text"
                      value={formAData.accountNumber}
                      onChange={(e) => setFormAData({...formAData, accountNumber: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      value={formAData.bankName}
                      onChange={(e) => setFormAData({...formAData, bankName: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Branch
                    </label>
                    <input
                      type="text"
                      value={formAData.bankBranch}
                      onChange={(e) => setFormAData({...formAData, bankBranch: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      IFSC Code
                    </label>
                    <input
                      type="text"
                      value={formAData.ifscCode}
                      onChange={(e) => setFormAData({...formAData, ifscCode: e.target.value.toUpperCase()})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm uppercase"
                      maxLength="11"
                      placeholder="SBIN0001234"
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Cheque / DD Payable Details
                    </label>
                    <input
                      type="text"
                      value={formAData.chequePayableDetails}
                      onChange={(e) => setFormAData({...formAData, chequePayableDetails: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      placeholder="Account holder name"
                    />
                  </div>
                </div>
              </div>

              {/* Section H: Declaration by the Insured */}
              <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
                <h2 className="text-2xl font-semibold mb-5 pb-4 border-b-2 border-[#9DC9D4]/30 flex items-center text-gray-800">
                  <CheckSquare className="w-6 h-6 mr-2 text-[#2D869C]" />
                  H. Declaration by the Insured (Optional)
                </h2>
                
                <div className="mb-5 p-5 bg-gray-50 rounded-xl border-2 border-gray-200 text-base text-gray-700 leading-relaxed">
                  <p>
                    I hereby declare that the information furnished in the claim form is true & correct to the best of my knowledge and belief. 
                    If I have made any false or untrue statement, suppression or concealment of any material fact with respect to questions asked 
                    in relation to this claim, my right to claim reimbursement shall be forfeited. I also consent & authorize TPA / Insurance Company, 
                    to seek necessary medical information / documents from any hospital / Medical Practitioner who has attended on the person against 
                    whom this claim is made. I hereby declare that I have included all the bills / receipts for the purpose of this claim & that I will 
                    not be making any supplementary claim except the pre/post-hospitalization claim, if any.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Date
                    </label>
                    <input
                      type="date"
                      value={formAData.declarationDate}
                      onChange={(e) => setFormAData({...formAData, declarationDate: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Place
                    </label>
                    <input
                      type="text"
                      value={formAData.declarationPlace}
                      onChange={(e) => setFormAData({...formAData, declarationPlace: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>

                  <div>
                    <label className="block text-lg font-medium text-gray-700 mb-2">
                      Signature of the Insured (Optional - can be signed after printing)
                    </label>
                    <input
                      type="text"
                      value={formAData.declarationSignature}
                      onChange={(e) => setFormAData({...formAData, declarationSignature: e.target.value})}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                      placeholder="Type name to sign (optional)"
                    />
                  </div>
                </div>

                <div className="mt-4 p-4 bg-yellow-50 border-2 border-yellow-300 rounded-xl">
                  <p className="text-base text-yellow-800 font-semibold">
                    <strong>Important:</strong> Please ensure all sections are filled accurately. Incomplete forms may delay processing.
                  </p>
                </div>
              </div>

              {/* Save Button for Form A */}
              <div className="flex justify-end gap-4 print:hidden">
                <button
                  type="submit"
                  disabled={loading || !selectedPatient}
                  className="flex items-center px-6 py-3 bg-[#2D869C] text-white rounded-xl hover:bg-[#2D869C]/90 hover:shadow-lg transition-all font-semibold shadow-md disabled:opacity-50 text-lg"
                >
                  <Save className="w-6 h-6 mr-3 text-white" />
                  {loading ? 'Saving...' : 'Save Form A'}
                </button>
              </div>
            </div>
          )}
        </form>
        ) : (
          /* Saved Forms List */
          <div className="space-y-4">
            {/* Filters */}
            <div className="bg-white rounded-xl border-2 border-gray-200 p-6">
              <div className="flex gap-5">
                <div className="flex-1">
                  <label className="block text-lg font-medium text-gray-700 mb-2">
                    Search Patient (auto-search)
                  </label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Name, UHID, Mobile..."
                      value={savedFormsSearch}
                      onChange={(e) => setSavedFormsSearch(e.target.value)}
                      className="w-full pl-12 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] text-lg font-medium text-gray-700 hover:border-[#2D869C] transition-all shadow-sm"
                    />
                  </div>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setSavedFormsPage(1);
                      loadSavedForms();
                    }}
                    className="px-6 py-3 bg-[#2D869C] text-white rounded-xl hover:bg-[#2D869C]/90 hover:shadow-lg transition-all font-semibold flex items-center shadow-md text-lg"
                  >
                    <Search className="w-5 h-5 mr-2" />
                    Search
                  </button>
                </div>
              </div>
            </div>

            {/* Forms List */}
            <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
              {loadingSavedForms ? (
                <div className="p-16 text-center">
                  <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#2D869C] mx-auto"></div>
                  <p className="mt-6 text-gray-600 font-semibold text-lg">Loading saved forms...</p>
                </div>
              ) : savedForms.length === 0 ? (
                <div className="p-16 text-center">
                  <FileText className="w-24 h-24 mx-auto mb-6 text-[#9DC9D4]" />
                  <p className="text-2xl font-bold text-gray-800 mb-2">No saved forms found</p>
                  <p className="text-base text-gray-500">Create a new form to get started</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-[#9DC9D4]/10 border-b-2 border-[#2D869C]/20">
                      <tr>
                        <th className="px-6 py-4 text-left text-sm font-bold text-[#2D869C] uppercase tracking-wider">
                          Form Type
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-[#2D869C] uppercase tracking-wider">
                          Patient
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-[#2D869C] uppercase tracking-wider">
                          {formType === 'A' ? 'Policy No' : 'IP Number'}
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-[#2D869C] uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-[#2D869C] uppercase tracking-wider">
                          Created
                        </th>
                        <th className="px-6 py-4 text-left text-sm font-bold text-[#2D869C] uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {savedForms.map((form) => (
                        <tr key={form._id} className="hover:bg-[#9DC9D4]/5 transition-all border-l-4 border-transparent hover:border-[#2D869C]">
                          <td className="px-6 py-5 whitespace-nowrap">
                            <span className={`px-4 py-2 text-sm font-bold rounded-xl shadow-sm ${
                              form.formType === 'A' ? 'bg-purple-100 text-purple-700 border-2 border-purple-200' : 'bg-[#9DC9D4]/30 text-[#2D869C] border-2 border-[#2D869C]/30'
                            }`}>
                              Form {form.formType}
                            </span>
                          </td>
                          <td className="px-6 py-5">
                            <div className="text-base font-semibold text-gray-800">
                              {form.patientDetails?.patientName || form.patientDetails?.name}
                            </div>
                          </td>
                          <td className="px-6 py-5 text-base text-gray-700 font-medium">
                            {formType === 'A' 
                              ? (form.primaryInsured?.policyNo || '-')
                              : (form.patientDetails?.ipRegistrationNumber || '-')
                            }
                          </td>
                          <td className="px-6 py-5">
                            <span className="text-lg font-bold text-[#2D869C]">
                              ?{(form.patientDetails?.totalClaimedAmount || form.claimDetails?.totalClaimAmount || 0).toLocaleString()}
                            </span>
                          </td>
                          <td className="px-6 py-5 text-base text-gray-600 font-medium">
                            {new Date(form.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </td>
                          <td className="px-6 py-5">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleViewForm(form._id, form.formType)}
                                className="p-2.5 text-[#2D869C] hover:bg-[#9DC9D4]/20 rounded-lg transition-all border-2 border-transparent hover:border-[#2D869C] shadow-sm"
                                title="View Form"
                              >
                                <Eye className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handlePrintForm(form)}
                                className="p-2.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-all border-2 border-transparent hover:border-gray-400 shadow-sm"
                                title="Print Form"
                              >
                                <Printer className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDownloadForm(form)}
                                className="p-2.5 text-green-600 hover:bg-green-50 rounded-lg transition-all border-2 border-transparent hover:border-green-500 shadow-sm"
                                title="Download PDF"
                              >
                                <Download className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleDeleteForm(form._id, form.formType)}
                                className="p-2.5 text-red-600 hover:bg-red-50 rounded-lg transition-all border-2 border-transparent hover:border-red-500 shadow-sm"
                                title="Delete Form"
                              >
                                <Trash2 className="w-5 h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {savedFormsPagination && savedFormsPagination.totalPages > 1 && (
                <div className="px-6 py-5 border-t-2 border-[#2D869C]/20 bg-[#9DC9D4]/5 flex items-center justify-between">
                  <div className="text-base text-gray-700 font-semibold">
                    Showing page <span className="text-[#2D869C]">{savedFormsPagination.currentPage}</span> of <span className="text-[#2D869C]">{savedFormsPagination.totalPages}</span>
                    <span className="text-gray-500 ml-2">({savedFormsPagination.totalItems} total forms)</span>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setSavedFormsPage(savedFormsPage - 1)}
                      disabled={savedFormsPage === 1}
                      className="px-6 py-3 bg-white border-2 border-[#2D869C]/30 text-[#2D869C] rounded-xl hover:bg-[#2D869C] hover:text-white hover:border-[#2D869C] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-[#2D869C] font-semibold transition-all shadow-sm text-base"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setSavedFormsPage(savedFormsPage + 1)}
                      disabled={savedFormsPage === savedFormsPagination.totalPages}
                      className="px-6 py-3 bg-white border-2 border-[#2D869C]/30 text-[#2D869C] rounded-xl hover:bg-[#2D869C] hover:text-white hover:border-[#2D869C] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-[#2D869C] font-semibold transition-all shadow-sm text-base"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClaimFormB;


